import React, { useEffect, useState, useMemo, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { supabase } from '../../supabaseClient'
import {
    AreaChart, Area,
    BarChart, Bar,
    ComposedChart, Line,
    PieChart, Pie,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell, ReferenceLine
} from "recharts"

import * as XLSX from "xlsx"
import { PaymentMethodsBreakdown } from '../Admin/DashboardOverview'
import { useBranchAIHistory } from '../../hooks/useBranchAIHistory'
import { usePaymentMethods, summarizePaymentMethods, formatPaymentLabel } from '../../hooks/usePaymentMethods'
import { authHeaders } from '../../utils/authFetch'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'

const C = {
    espresso: "transparent",
    dark: "#F9FAFB",
    card: "#FFFFFF",
    border: "#E5E7EB",
    muted: "#9CA3AF",
    text: "#111827",
    textSoft: "#6B7280",
    amber: "#D97706",
    gold: "#B45309",
    teal: "#0D9488",
    coral: "#DC2626",
    blue: "#2563EB",
    green: "#16A34A",
    purple: "#7C3AED",
}

// Accent color for the AI Analyst feature on this (branch) side of the app.
// Super Admin uses C.purple for its AI chrome — we use C.blue here so the
// two surfaces are visually distinct while sharing the same structure.
const AI = C.blue

const ROLE_PERMISSIONS = {
    branch_admin: {
        label: "Branch Admin",
        badgeColor: C.teal,
        showSalesRevenue: true,  
        showPaymentMethods: true,  
        showPeakHours: true,       
        showMarketingROI: true,   
        showMenuCatalog: true,   
        showVoidedItems: true,
        showRewardsPullouts: true,
        showInventoryVariance: true,
        showRemakeRequests: true,
        showAIAnalyst: true,
    },
    staff: {
        label: "Staff",
        badgeColor: C.amber,
        showSalesRevenue: false,
        showPaymentMethods: false,   
        showPeakHours: true,       
        showMarketingROI: false, 
        showMenuCatalog: true, 
        showVoidedItems: false,
        showRewardsPullouts: false,
        showInventoryVariance: false,
        showRemakeRequests: true,
        showAIAnalyst: false,
    },
}

const fmt = (n) => `₱${Number(n).toLocaleString("en-PH", { minimumFractionDigits: 0 })}`
const hasData = (d) => Array.isArray(d) && d.length > 0
const PHT_OFFSET_MS = 8 * 60 * 60 * 1000
const toPHT = (isoStr) => new Date(new Date(isoStr).getTime() + PHT_OFFSET_MS)
const fmtDate = (d) => d.toLocaleDateString("en-PH", { month: "short", day: "numeric", timeZone: "UTC" })
const fmtHour = (h) => {
    if (h === 0) return "12am"
    if (h < 12) return `${h}am`
    if (h === 12) return "12pm"
    return `${h - 12}pm`
}
const presetStart = (range) => {
    const days = { "7d": 7, "30d": 30, "90d": 90, "1y": 365 }[range] ?? 30
    return new Date(Date.now() - days * 86400000).toISOString()
}

function buildSalesData(orders) {
    const map = {}
    for (const o of orders) {
        const key = fmtDate(toPHT(o.created_at))
        if (!map[key]) map[key] = { date: key, revenue: 0, orders: 0 }
        map[key].revenue += Number(o.final_amount)
        map[key].orders += 1
    }
    return Object.values(map)
}

function buildPeakHoursData(orders) {
    const map = {}
    for (const o of orders) {
        const h = toPHT(o.created_at).getUTCHours()
        const key = fmtHour(h)
        if (!map[key]) map[key] = { hour: key, _h: h, orders: 0, revenue: 0 }
        map[key].orders += 1
        map[key].revenue += Number(o.final_amount)
    }
    return Object.values(map).sort((a, b) => a._h - b._h)
}

function useBranchName(branchId) {
    const [name, setName] = useState(null)
    useEffect(() => {
        if (!branchId) return
        supabase
            .from("branches")
            .select("name")
            .eq("branch_id", branchId)
            .single()
            .then(({ data }) => setName(data?.name ?? "Your Branch"))
    }, [branchId])
    return name
}

function useMenuItems() {
    const [items, setItems] = useState(null)
    useEffect(() => {
        supabase
            .from("menu_items")
            .select("item_id, name, category, classification, item_price")
            .eq("is_archived", false)
            .order("item_price", { ascending: false })
            .then(({ data }) => setItems(data ?? []))
    }, [])
    return items
}

function useMarketing(startISO, endISO, branchId, enabled) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!enabled) return
        let cancelled = false
        setData(null)
        let query = supabase
            .from("marketing_campaigns")
            .select("channel, spend, campaign_conversions(revenue_attributed)")
            .gte("start_date", startISO)
        if (branchId) query = query.eq("branch_id", branchId)
        query.then(({ data: rows, error }) => {
            if (cancelled) return
            if (error || !rows) { setData([]); return }
            const map = {}
            for (const row of rows) {
                const ch = row.channel ?? "Other"
                if (!map[ch]) map[ch] = { channel: ch, spend: 0, revenue: 0 }
                map[ch].spend += Number(row.spend ?? 0)
                map[ch].revenue += (row.campaign_conversions ?? [])
                    .reduce((s, cv) => s + Number(cv.revenue_attributed ?? 0), 0)
            }
            setData(Object.values(map))
        })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId, enabled])
    return data
}

function useSalesData(startISO, endISO, branchId, enabled) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!enabled) return
        let cancelled = false
        setData(null)
        let query = supabase
            .from("orders")
            .select("final_amount, created_at")
            .eq("order_status", "completed")
            .gte("created_at", startISO)
            .order("created_at", { ascending: true })
        if (endISO) query = query.lte("created_at", endISO)
        if (branchId) query = query.eq("branch_id", branchId)
        query.then(({ data: rows }) => {
            if (cancelled) return
            setData(buildSalesData(rows ?? []))
        })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId, enabled])
    return data
}

function usePeakHoursData(startISO, endISO, branchId, enabled) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!enabled) return
        let cancelled = false
        setData(null)
        let query = supabase
            .from("orders")
            .select("final_amount, created_at")
            .eq("order_status", "completed")
            .gte("created_at", startISO)
        if (branchId) query = query.eq("branch_id", branchId)
        query.then(({ data: rows }) => {
            if (cancelled) return
            setData(buildPeakHoursData(rows ?? []))
        })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId, enabled])
    return data
}

function useStaffSummary(startISO, endISO, branchId, enabled) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!enabled || !branchId) return
        let cancelled = false
        setData(null)
        supabase
            .from("orders")
            .select("staff_id, final_amount, profiles(full_name)")
            .eq("order_status", "completed")
            .eq("branch_id", branchId)
            .gte("created_at", startISO)
            .then(({ data: rows }) => {
                if (cancelled) return
                const map = {}
                for (const o of rows ?? []) {
                    const id = o.staff_id
                    const name = o.profiles?.full_name ?? "Unknown"
                    if (!map[id]) map[id] = { name, orders: 0, revenue: 0 }
                    map[id].orders += 1
                    map[id].revenue += Number(o.final_amount)
                }
                setData(Object.values(map).sort((a, b) => b.orders - a.orders))
            })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId, enabled])
    return data
}

function useVoidedItems(startISO, endISO, branchId, enabled) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!enabled || !branchId) return
        let cancelled = false
        setData(null)
        let query = supabase
            .from("order_items")
            .select("quantity, item_price, void_reason, void_requested_at, void_decided_at, created_at, item_id, menu_items:item_id (name, category), orders!inner(branch_id)")
            .eq("line_status", "voided")
            .eq("orders.branch_id", branchId)
            .gte("created_at", startISO)
        if (endISO) query = query.lte("created_at", endISO)
        query.then(({ data: rows }) => {
            if (cancelled) return
            setData(rows ?? [])
        })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId, enabled])
    return data
}

function useRemakeRequests(startISO, endISO, branchId, enabled) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!enabled || !branchId) return
        let cancelled = false
        setData(null)
        let query = supabase
            .from("remake_requests")
            .select("remake_request_id, reason, status, created_at")
            .eq("branch_id", branchId)
            .gte("created_at", startISO)
        if (endISO) query = query.lte("created_at", endISO)
        query.then(({ data: rows, error }) => {
            if (cancelled) return
            if (error) { console.error("remake-requests error:", error); setData([]); return }
            setData(rows ?? [])
        })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId, enabled])
    return data
}

function useInventoryDiscrepancies(startISO, endISO, branchId, enabled) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!enabled || !branchId) return
        let cancelled = false
        setData(null)
        const params = new URLSearchParams({ startISO, ...(endISO && { endISO }), branchId })
        fetch(`${BASE_URL}/api/inventory-discrepancies?${params}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(rows => { if (!cancelled) setData(rows ?? []) })
            .catch(err => { console.error("inventory-discrepancies error:", err); if (!cancelled) setData([]) })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId, enabled])
    return data
}

function useRewardsAndPullouts(startISO, endISO, branchId, enabled) {
    const [redemptions, setRedemptions] = useState(null)
    const [pullouts, setPullouts] = useState(null)
    useEffect(() => {
        if (!enabled) return
        let cancelled = false
        setRedemptions(null)
        setPullouts(null)
        const params = new URLSearchParams({
            startISO,
            ...(endISO && { endISO }),
            ...(branchId && { branchId }),
        })
        fetch(`${BASE_URL}/api/rewards-pullouts?${params}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(({ redemptions, pullouts }) => {
                if (cancelled) return
                setRedemptions(redemptions ?? [])
                setPullouts(pullouts ?? [])
            })
            .catch(err => {
                console.error("rewards-pullouts error:", err)
                if (cancelled) return
                setRedemptions([])
                setPullouts([])
            })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId, enabled])
    return { redemptions, pullouts }
}

const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ background: C.card, borderColor: C.border }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-5">
        <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
    </div>
)

const KPI = ({ label, value, sub, color = C.amber }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="mt-1 mb-0.5 text-[22px] font-bold leading-tight" style={{ color }}>{value}</p>
        {sub && <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{sub}</p>}
    </div>
)

const EmptyKPI = ({ label }) => (
    <div className="rounded-xl p-3.5 min-w-0 border border-dashed" style={{ borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="m-0 text-[10px]" style={{ color: C.border }}>No data yet</p>
    </div>
)

const EmptyState = ({ message = "No data available for this period.", height = 240 }) => (
    <div className="flex flex-col items-center justify-center gap-2.5 rounded-xl border border-dashed"
        style={{ height, borderColor: C.border }}>
        <p className="m-0 text-[13px] text-center max-w-[256px]" style={{ color: C.muted }}>{message}</p>
        <p className="m-0 text-[11px]" style={{ color: C.border }}>Data will appear here once records are added</p>
    </div>
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
        <div className="rounded-lg px-3 py-2 text-xs border shadow-sm"
            style={{ background: "#FFFFFF", borderColor: C.border }}>
            <p className="m-0 mb-1.5 font-semibold" style={{ color: C.text }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="m-0 my-0.5" style={{ color: p.color || C.textSoft }}>
                    {p.name} : <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
                </p>
            ))}
        </div>
    )
}

const RangeSelector = ({ range, setRange, customStart, setCustomStart, customEnd, setCustomEnd  }) => {
    const options = [
        { label: "7 days", value: "7d" },
        { label: "30 days", value: "30d" },
        { label: "90 days", value: "90d" },
        { label: "1 year", value: "1y" },
        { label: "Custom", value: "custom" } 
    ]

    const handlePreset = (value) => {
        setRange(value)
        setCustomStart("")
        setCustomEnd("")
    }
    
    const hasCustom = customStart && customEnd 
    const isCustomActive = range === 'custom' || hasCustom

        return (
         <div className="flex flex-col gap-2 items-end min-w-0 w-full sm:w-auto">
            {/* Preset buttons — horizontal scroll instead of wrap */}
            <div
                className="flex gap-1.5 overflow-x-auto"
                style={{ maxWidth: "100%", WebkitOverflowScrolling: "touch", scrollbarWidth: "thin" }}
            >
                {options.map((o) => (
                    <button key={o.value} onClick={() => handlePreset(o.value)}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors flex-shrink-0"
                        style={{
                            background: range === o.value && !hasCustom ? C.amber : C.card,
                            borderColor: range === o.value && !hasCustom ? C.amber : C.border,
                            color: range === o.value && !hasCustom ? "#fff" : C.textSoft,
                            cursor: "pointer",
                        }}>
                        {o.label}
                    </button>
                ))}
            </div>

            {isCustomActive && (
                <div className="flex items-center gap-2 flex-wrap justify-end pt-1.5 pb-1 border-t border-dashed"
                    style={{ borderColor: C.border }}>
                    <span className="text-[11px]" style={{ color: C.muted }}>From:</span>
                    <input
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{
                            borderColor: hasCustom ? C.amber : C.border,
                            color: C.text,
                            background: C.card,
                        }}
                    />
                    <span className="text-[11px]" style={{ color: C.muted }}>To:</span>
                    <input
                        type="date"
                        value={customEnd}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{
                            borderColor: hasCustom ? C.amber : C.border,
                            color: C.text,
                            background: C.card,
                        }}
                    />
                    {hasCustom && (
                        <button
                            onClick={() => { 
                                setCustomStart(""); 
                                setCustomEnd(""); 
                                setRange("30d") 
                            }}
                            className="text-[11px] px-2 py-1 rounded-full border"
                            style={{ 
                                borderColor: C.border, 
                                color: C.muted, 
                                cursor: "pointer", 
                                background: C.card 
                            }}>
                            ✕ Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

const RoleBadge = ({ role, branchName }) => {
    const cfg = ROLE_PERMISSIONS[role] ?? ROLE_PERMISSIONS.staff
    const shortName = branchName?.replace("Coffee Gus·to ", "") ?? ""
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border"
                style={{
                    background: cfg.badgeColor + "18",
                    borderColor: cfg.badgeColor + "44",
                    color: cfg.badgeColor,
                }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.badgeColor }} />
                {cfg.label}
            </span>
            {shortName && (
                <span className="text-[12px]" style={{ color: C.textSoft }}>
                    Viewing: <strong style={{ color: C.text }}>{shortName}</strong>
                </span>
            )}
        </div>
    )
}

const SalesRevenue = ({ data, loading }) => {
    const empty = !hasData(data)
    const totalRevenue = empty ? 0 : data.reduce((s, d) => s + d.revenue, 0)
    const totalOrders = empty ? 0 : data.reduce((s, d) => s + d.orders, 0)
    const avgTicket = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : null
    const growth = !empty && data.length >= 2
        ? (((data[data.length - 1].revenue - data[0].revenue) / (data[0].revenue || 1)) * 100).toFixed(1)
        : null

    return (
        <Card>
            <SectionTitle title="Sales & Revenue" subtitle="Daily performance — your branch only" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Revenue" /><EmptyKPI label="Total Orders" /><EmptyKPI label="Avg Ticket" /><EmptyKPI label="Period Growth" /></>
                ) : (
                    <>
                        <KPI label="Total Revenue" value={fmt(totalRevenue)} color={C.green} />
                        <KPI label="Total Orders" value={totalOrders.toLocaleString()} color={C.blue} />
                        <KPI label="Avg Ticket" value={avgTicket ? fmt(avgTicket) : "—"} color={C.amber} />
                        <KPI label="Period Growth"
                            value={growth !== null ? `${parseFloat(growth) >= 0 ? "+" : ""}${growth}%` : "—"}
                            color={growth !== null && parseFloat(growth) >= 0 ? C.teal : C.coral}
                        />
                    </>
                )}
            </div>

            {loading ? <Spinner height={240} /> : empty ? (
                <EmptyState message="No completed orders found for this period." height={240} />
            ) : (
                <ResponsiveContainer debounce={200} width="100%" height={240}>
                    <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={C.teal} stopOpacity={0.4} />
                                <stop offset="95%" stopColor={C.teal} stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="ordGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={C.amber} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={C.amber} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis yAxisId="rev" tick={{ fill: C.muted, fontSize: 9 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                        <YAxis yAxisId="ord" orientation="right" tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                        <Tooltip content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null
                            return (
                                <div className="rounded-lg px-3 py-2 text-xs border shadow-sm"
                                    style={{ background: "#FFFFFF", borderColor: C.border }}>
                                    <p className="m-0 mb-1 font-semibold" style={{ color: C.text }}>{label}</p>
                                    {payload.map((p, i) => (
                                        <p key={i} className="m-0 my-0.5" style={{ color: p.color }}>
                                            {p.name}: <strong>{p.name === "Revenue" ? fmt(p.value) : p.value}</strong>
                                        </p>
                                    ))}
                                </div>
                            )
                        }} />
                        <Legend wrapperStyle={{ fontSize: 12, color: C.textSoft, paddingTop: 6 }} />
                        <Area yAxisId="rev" type="monotone" dataKey="revenue" name="Revenue" stroke={C.teal} fill="url(#revGrad)" strokeWidth={2} dot={false} />
                        <Area yAxisId="ord" type="monotone" dataKey="orders" name="Orders" stroke={C.amber} fill="url(#ordGrad)" strokeWidth={2} dot={false} />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </Card>
    )
}

const HEAT_COLORS = ["#FEF3C7", "#FDE68A", "#FCD34D", "#FBBF24", "#F59E0B", "#D97706", "#92400E"]
const heatColor = (orders, max) => {
    const t = max === 0 ? 0 : orders / max
    const idx = Math.min(Math.floor(t * (HEAT_COLORS.length - 1)), HEAT_COLORS.length - 2)
    return HEAT_COLORS[idx + 1]
}

const PeakHourAnalytics = ({ data, loading, role }) => {
    const empty = !hasData(data)
    const isStaff = role === "staff"
    const maxOrders = empty ? 0 : Math.max(...data.map((d) => d.orders))
    const peakHour = empty ? null : data.find((d) => d.orders === maxOrders)
    const totalOrders = empty ? 0 : data.reduce((s, d) => s + d.orders, 0)
    const avgOrders = empty ? "—" : (totalOrders / data.length).toFixed(0)

    return (
        <Card>
            <SectionTitle
                title="Peak Hour Analytics"
                subtitle={isStaff
                    ? "Hourly order volume — use this to plan your shift"
                    : "Hourly order volume & revenue (PHT)"
                }
            />

            {isStaff && peakHour && (
                <div className="mb-4 px-3 py-2.5 rounded-xl border text-[12px] flex items-start gap-2"
                    style={{ background: C.amber + "12", borderColor: C.amber + "33", color: C.gold }}>
                    <span>
                        Busiest hour is <strong>{peakHour.hour}</strong> with {peakHour.orders} orders — ensure you're prepared before this window.
                    </span>
                </div>
            )}

            <div className="grid grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Peak Hour" /><EmptyKPI label="Total Orders" /><EmptyKPI label="Avg / Hour" /></>
                ) : (
                    <>
                        <KPI label="Peak Hour" value={peakHour?.hour} sub={`${peakHour?.orders} orders`} color={C.gold} />
                        <KPI label="Total Orders" value={totalOrders.toLocaleString()} sub="across all hours" color={C.teal} />
                        <KPI label="Avg / Hour" value={avgOrders} sub="orders per hour" color={C.blue} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={240} /> : empty ? (
                <EmptyState message="No order time data available for this period." height={240} />
            ) : (
                <>
                    <ResponsiveContainer debounce={200} width="100%" height={240}>
                        <BarChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="hour" tick={{ fill: C.textSoft, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <Tooltip content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null
                                return (
                                    <div className="rounded-lg px-3 py-2 text-xs border shadow-sm"
                                        style={{ background: "#FFFFFF", borderColor: C.border }}>
                                        <p className="m-0 mb-1 font-semibold" style={{ color: C.text }}>{label}</p>
                                        <p className="m-0" style={{ color: C.amber }}>
                                            Orders: <strong>{payload[0]?.value}</strong>
                                        </p>
                                        {!isStaff && (
                                            <p className="m-0" style={{ color: C.teal }}>
                                                Revenue: <strong>{fmt(Math.round(payload[0]?.payload?.revenue))}</strong>
                                            </p>
                                        )}
                                    </div>
                                )
                            }} />
                            <ReferenceLine y={parseInt(avgOrders)} stroke={C.purple} strokeDasharray="4 4"
                                label={{ value: "avg", fill: C.purple, fontSize: 10 }} />
                            <Bar dataKey="orders" radius={[5, 5, 0, 0]}>
                                {data.map((entry) => (
                                    <Cell key={entry.hour} fill={heatColor(entry.orders, maxOrders)} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="mt-3">
                        <p className="text-[11px] m-0 mb-1.5" style={{ color: C.muted }}>HEAT INTENSITY</p>
                        <div className="flex items-center gap-1">
                            <span className="text-[10px]" style={{ color: C.muted }}>Low</span>
                            <div className="flex flex-1 h-2 rounded overflow-hidden">
                                {HEAT_COLORS.slice(1).map((c, i) => <div key={i} className="flex-1" style={{ background: c }} />)}
                            </div>
                            <span className="text-[10px]" style={{ color: C.gold }}>Peak</span>
                        </div>
                    </div>
                </>
            )}
        </Card>
    )
}

const MarketingROI = ({ data, loading }) => {
    const empty = !hasData(data)
    const processed = empty ? [] : data.map((d) => ({
        ...d,
        roi: (((d.revenue - d.spend) / d.spend) * 100).toFixed(1),
        roi_val: ((d.revenue - d.spend) / d.spend) * 100,
    }))
    const totalSpend = empty ? null : data.reduce((s, d) => s + d.spend, 0)
    const totalRevenue = empty ? null : data.reduce((s, d) => s + d.revenue, 0)
    const blendedROI = totalSpend && totalSpend > 0
        ? (((totalRevenue - totalSpend) / totalSpend) * 100).toFixed(1)
        : null

    return (
        <Card>
            <SectionTitle title="Marketing ROI" subtitle="Channel spend vs. revenue attributed — your branch only" />
            <div className="grid grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Spend" /><EmptyKPI label="Revenue Driven" /><EmptyKPI label="Blended ROI" /></>
                ) : (
                    <>
                        <KPI label="Total Spend" value={fmt(totalSpend)} color={C.coral} />
                        <KPI label="Revenue Driven" value={fmt(totalRevenue)} color={C.green} />
                        <KPI label="Blended ROI" value={blendedROI ? `${blendedROI}%` : "—"} color={C.amber} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={240} /> : empty ? (
                <EmptyState message="No marketing campaign data yet." height={240} />
            ) : (
                <>
                    <ResponsiveContainer debounce={200} width="100%" height={240}>
                        <ComposedChart data={processed} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="channel" tick={{ fill: C.textSoft, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis yAxisId="money" tick={{ fill: C.muted, fontSize: 10 }} tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                            <YAxis yAxisId="roi" orientation="right" tick={{ fill: C.amber, fontSize: 10 }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 12, color: C.textSoft, paddingTop: 8 }} />
                            <Bar yAxisId="money" dataKey="spend" name="Spend" fill={C.coral} radius={[4, 4, 0, 0]} opacity={0.85} />
                            <Bar yAxisId="money" dataKey="revenue" name="Revenue" fill={C.teal} radius={[4, 4, 0, 0]} opacity={0.85} />
                            <Line yAxisId="roi" dataKey="roi_val" name="ROI %" stroke={C.gold} strokeWidth={2.5} dot={{ fill: C.gold, r: 4 }} type="monotone" />
                        </ComposedChart>
                    </ResponsiveContainer>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {[...processed].sort((a, b) => b.roi_val - a.roi_val).map((d, i) => (
                            <span key={d.channel}
                                className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] border"
                                style={{
                                    background: i === 0 ? C.amber + "22" : C.dark,
                                    borderColor: i === 0 ? C.amber : C.border,
                                    color: i === 0 ? C.amber : C.textSoft,
                                }}>
                                {d.channel}{" "}
                                <strong style={{ color: i === 0 ? C.gold : C.text }}>{d.roi}% ROI</strong>
                            </span>
                        ))}
                    </div>
                </>
            )}
        </Card>
    )
}

const CAT_COLORS = { Coffee: C.amber, "Non-Coffee": C.teal }

const MenuCatalog = ({ data, loading }) => {
    const empty = !hasData(data)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [filterCat, setFilterCat] = useState("All")
    const [filterCls, setFilterCls] = useState("All")
    const [searchQuery, setSearchQuery] = useState("")
    const [availableClassifications, setAvailableClassifications] = useState([])
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 12

    const categories = useMemo(() => {
        if (empty) return []
        return [...new Set(data.map(i => i.category).filter(Boolean))].sort()
    }, [data])

    const filtered = useMemo(() => {
        if (empty) return []
        return data.filter(item => {
            const matchCat = filterCat === "All" || item.category === filterCat
            const matchCls = filterCls === "All" || item.classification === filterCls
            const matchSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase())
            return matchCat && matchCls && matchSearch
        })
    }, [data, filterCat, filterCls, searchQuery])

    useEffect(() => setPage(1), [filterCat, filterCls, searchQuery])

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    return (
        <Card>
            <div
                className="flex items-center justify-between cursor-pointer select-none"
                onClick={() => setIsCollapsed(v => !v)}
            >
                <div>
                    <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>
                        Menu Catalog
                    </h2>
                    <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>
                        All active items available at your branch
                    </p>
                </div>
                <svg
                    xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2"
                    className="flex-shrink-0 transition-transform duration-200"
                    style={{
                        color: C.muted,
                        transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)"
                    }}
                >
                    <path d="M6 9l6 6 6-6"/>
                </svg>
            </div>

            {!isCollapsed && (
                <>
                    {loading ? <Spinner height={200} /> : empty ? (
                        <EmptyState message="No menu items found." height={300} />
                    ) : (
                        <>
                            {/* Category filter */}
                            <div className="flex flex-wrap gap-1.5 mt-4">
                                {["All", ...categories].map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => {
                                            setFilterCat(cat)
                                            setFilterCls("All")
                                            if (cat === "All") {
                                                setAvailableClassifications([])
                                            } else {
                                                const cls = [...new Set(
                                                    data
                                                        .filter(i => i.category === cat)
                                                        .map(i => i.classification)
                                                        .filter(Boolean)
                                                )].sort()
                                                setAvailableClassifications(cls)
                                            }
                                        }}
                                        className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                                        style={{
                                            background: filterCat === cat ? C.amber : C.card,
                                            borderColor: filterCat === cat ? C.amber : C.border,
                                            color: filterCat === cat ? "#fff" : C.textSoft,
                                            cursor: "pointer",
                                        }}
                                    >
                                        {cat}
                                        {cat !== "All" && (
                                            <span style={{ marginLeft: 4, opacity: 0.75, fontSize: 10 }}>
                                                ({data.filter(i => i.category === cat).length})
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Classification filter */}
                            {filterCat !== "All" && availableClassifications.length > 0 && (
                                <div className="flex items-center flex-wrap gap-1.5 mt-2 pl-1">
                                    <span className="text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>
                                        {filterCat}:
                                    </span>
                                    {["All", ...availableClassifications].map(cls => (
                                        <button
                                            key={cls}
                                            onClick={() => setFilterCls(cls)}
                                            className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold border transition-all"
                                            style={{
                                                background: filterCls === cls ? C.text : C.card,
                                                borderColor: filterCls === cls ? C.text : C.border,
                                                color: filterCls === cls ? "#fff" : C.textSoft,
                                                cursor: "pointer",
                                            }}
                                        >
                                            {cls === "All" ? `All ${filterCat}` : cls}
                                            {cls !== "All" && (
                                                <span style={{ marginLeft: 3, opacity: 0.7, fontSize: 9 }}>
                                                    ({data.filter(i => i.category === filterCat && i.classification === cls).length})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Search + count row */}
                            <div className="flex items-center justify-between mt-3 mb-3 gap-3">
                                <p className="text-[11px] uppercase tracking-wider m-0" style={{ color: C.muted }}>
                                    {filtered.length} / {data.length} Items
                                </p>
                                <div className="relative">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                        strokeLinecap="round" strokeLinejoin="round"
                                        className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                                        style={{ color: C.muted }}
                                    >
                                        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search items..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 rounded-lg border text-[11px] outline-none"
                                        style={{
                                            borderColor: C.border,
                                            color: C.text,
                                            background: C.card,
                                            width: 160,
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Item grid */}
                            {filtered.length === 0 ? (
                                <EmptyState message="No items match your filters." height={120} />
                            ) : (
                                <>
                                    <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
                                        {paginated.map((item) => (
                                            <div key={item.item_id} className="rounded-xl p-3 border"
                                                style={{ background: C.dark, borderColor: C.border }}>
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full border"
                                                        style={{
                                                            background: (CAT_COLORS[item.category] || C.blue) + "22",
                                                            color: CAT_COLORS[item.category] || C.blue,
                                                            borderColor: (CAT_COLORS[item.category] || C.blue) + "44",
                                                        }}>
                                                        {item.category}
                                                    </span>
                                                    <span className="text-[10px]" style={{ color: C.muted }}>{item.classification}</span>
                                                </div>
                                                <p className="mt-1.5 mb-0.5 text-xs font-semibold leading-tight" style={{ color: C.text }}>
                                                    {item.name}
                                                </p>
                                                <p className="m-0 text-base font-bold" style={{ color: C.amber }}>
                                                    ₱{item.item_price}
                                                </p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="no-print flex items-center justify-between mt-4 pt-3 border-t"
                                            style={{ borderColor: C.border }}>
                                            <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                                            </p>
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                                    disabled={page === 1}
                                                    className="px-2.5 py-1 rounded-lg border text-[11px] transition-colors"
                                                    style={{
                                                        borderColor: C.border,
                                                        color: page === 1 ? C.border : C.textSoft,
                                                        background: C.card,
                                                        cursor: page === 1 ? "not-allowed" : "pointer",
                                                    }}
                                                >
                                                    ← Prev
                                                </button>

                                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                                                    .reduce((acc, n, idx, arr) => {
                                                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                                                        acc.push(n)
                                                        return acc
                                                    }, [])
                                                    .map((n, idx) =>
                                                        n === "…" ? (
                                                            <span key={`ellipsis-${idx}`} className="text-[11px] px-1" style={{ color: C.muted }}>…</span>
                                                        ) : (
                                                            <button
                                                                key={n}
                                                                onClick={() => setPage(n)}
                                                                className="w-7 h-7 rounded-lg border text-[11px] font-semibold transition-colors"
                                                                style={{
                                                                    background: page === n ? C.amber : C.card,
                                                                    borderColor: page === n ? C.amber : C.border,
                                                                    color: page === n ? "#fff" : C.textSoft,
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                {n}
                                                            </button>
                                                        )
                                                    )
                                                }

                                                <button
                                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                                    disabled={page === totalPages}
                                                    className="px-2.5 py-1 rounded-lg border text-[11px] transition-colors"
                                                    style={{
                                                        borderColor: C.border,
                                                        color: page === totalPages ? C.border : C.textSoft,
                                                        background: C.card,
                                                        cursor: page === totalPages ? "not-allowed" : "pointer",
                                                    }}
                                                >
                                                    Next →
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </Card>
    )
}

const REASON_LABELS = {
    order_cancelled:       "Order Cancelled",
    "order cancelled":     "Order Cancelled",
    wrong_item:            "Wrong Item",
    customer_changed_mind: "Customer Changed Mind",
    other:                 "Other",
}

const VOID_COLORS = [C.coral, "#E85D04", "#F48C06", "#FAA307"]

const VoidedItems = ({ data, loading }) => {
    const [itemPage, setItemPage] = useState(1)
    const ITEMS_PAGE_SIZE = 5
    const empty = !hasData(data)
    const totalVoided = empty ? 0 : data.length
    const revenueLost = empty ? 0 : data.reduce((s, d) => s + Number(d.item_price) * Number(d.quantity), 0)
    const normalizeReason = (r) => (r ?? "other").trim().toLowerCase().replace(/\s+/g, "_") || "other"

    const byReason = empty ? [] : Object.entries(
        data.reduce((acc, d) => {
            const key = normalizeReason(d.void_reason)
            if (!acc[key]) acc[key] = { reason: key, count: 0, lost: 0 }
            acc[key].count += 1
            acc[key].lost += Number(d.item_price) * Number(d.quantity)
            return acc
        }, {})
    ).map(([, v]) => v).sort((a, b) => b.count - a.count)

    const byItem = empty ? [] : Object.entries(
        data.reduce((acc, d) => {
            const name = d.menu_items?.name ?? `Item ${d.item_id?.slice(0, 8) ?? "Unknown"}`
            const category = d.menu_items?.category ?? "—"
            const key = d.item_id ?? name
            if (!acc[key]) acc[key] = { name, category, count: 0, lost: 0 }
            acc[key].count += Number(d.quantity)
            acc[key].lost += Number(d.item_price) * Number(d.quantity)
            return acc
        }, {})
    ).map(([, v]) => v).sort((a, b) => b.count - a.count)

    const itemTotalPages = Math.ceil(byItem.length / ITEMS_PAGE_SIZE)
    const paginatedItems = byItem.slice((itemPage - 1) * ITEMS_PAGE_SIZE, itemPage * ITEMS_PAGE_SIZE)
    const maxCount = byItem.length > 0 ? byItem[0].count : 1

    return (
        <Card>
            <SectionTitle title="Voided Items" subtitle="Reason breakdown & most frequently voided menu items" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Voids" /><EmptyKPI label="Revenue Lost" /><EmptyKPI label="Top Reason" /></>
                ) : (
                    <>
                        <KPI label="Total Voids" value={totalVoided.toLocaleString()} color={C.coral} />
                        <KPI label="Revenue Lost" value={fmt(revenueLost)} color={C.purple} />
                        <KPI label="Top Reason"
                            value={byReason[0] ? (REASON_LABELS[byReason[0].reason] ?? byReason[0].reason) : "—"}
                            sub={byReason[0] ? `${byReason[0].count} voids` : ""}
                            color={C.coral}
                        />
                    </>
                )}
            </div>

            {loading ? <Spinner height={280} /> : empty ? (
                <EmptyState message="No voided items for this period." height={280} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                            Voids by Reason
                        </p>
                        <ResponsiveContainer debounce={200} width="100%" height={220}>
                            <PieChart>
                                <Pie
                                    data={byReason}
                                    dataKey="count"
                                    nameKey="reason"
                                    cx="50%"
                                    cy="45%"
                                    innerRadius="52%"
                                    outerRadius="72%"
                                    paddingAngle={3}
                                    strokeWidth={0}
                                >
                                    {byReason.map((_, i) => (
                                        <Cell key={i} fill={VOID_COLORS[i % VOID_COLORS.length]} opacity={0.92} />
                                    ))}
                                </Pie>
                                <text x="50%" y="39%" textAnchor="middle" dominantBaseline="middle"
                                    style={{ fontSize: 20, fontWeight: 700, fill: C.text, fontFamily: "inherit" }}>
                                    {byReason.reduce((s, d) => s + d.count, 0)}
                                </text>
                                <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle"
                                    style={{ fontSize: 10, fill: C.muted, fontFamily: "inherit", letterSpacing: "0.06em" }}>
                                    total voids
                                </text>
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null
                                        const d = payload[0].payload
                                        const total = byReason.reduce((s, r) => s + r.count, 0)
                                        const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : "0"
                                        return (
                                            <div className="rounded-lg px-3 py-2 text-xs border shadow-sm"
                                                style={{ background: "#FFFFFF", borderColor: C.border }}>
                                                <p className="m-0 mb-1 font-semibold" style={{ color: C.text }}>
                                                    {REASON_LABELS[d.reason] ?? d.reason}
                                                </p>
                                                <p className="m-0" style={{ color: C.coral }}>
                                                    Voids: <strong>{d.count}</strong>
                                                    <span style={{ color: C.muted, marginLeft: 6 }}>({pct}%)</span>
                                                </p>
                                                <p className="m-0" style={{ color: C.purple }}>
                                                    Lost: <strong>{fmt(d.lost)}</strong>
                                                </p>
                                            </div>
                                        )
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>

                        <div className="flex flex-col gap-1.5 mt-1">
                            {byReason.map((d, i) => {
                                const total = byReason.reduce((s, r) => s + r.count, 0)
                                const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : "0"
                                return (
                                    <div key={d.reason}
                                        className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                                        style={{ background: C.dark, border: `1px solid ${C.border}` }}>
                                        <div className="flex items-center gap-2">
                                            <div style={{
                                                width: 8, height: 8, borderRadius: "50%",
                                                background: VOID_COLORS[i % VOID_COLORS.length],
                                                flexShrink: 0,
                                            }} />
                                            <span style={{ fontSize: 12, color: C.text }}>
                                                {REASON_LABELS[d.reason] ?? d.reason}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span style={{ fontSize: 11, color: C.muted }}>{pct}%</span>
                                            <span style={{
                                                fontSize: 11, fontWeight: 600,
                                                color: VOID_COLORS[i % VOID_COLORS.length],
                                                minWidth: 20, textAlign: "right",
                                            }}>
                                                ×{d.count}
                                            </span>
                                            <span style={{ fontSize: 11, color: C.purple, minWidth: 64, textAlign: "right" }}>
                                                {fmt(d.lost)}
                                            </span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[11px] uppercase tracking-wider m-0" style={{ color: C.muted }}>
                                Most Frequently Voided Items
                            </p>
                            {byItem.length > 0 && (
                                <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                    {byItem.length} unique item{byItem.length !== 1 ? "s" : ""}
                                </p>
                            )}
                        </div>

                        {byItem.length === 0 ? (
                            <EmptyState message="No item-level void data." height={180} />
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 8 }}>
                                <div className="rounded-xl border overflow-hidden"
                                    style={{ borderColor: C.border, flex: 1, display: "flex", flexDirection: "column" }}>
                                    <div className="grid text-[10px] uppercase tracking-wider px-3 py-2"
                                        style={{
                                            gridTemplateColumns: "1fr auto auto",
                                            background: C.dark,
                                            borderBottom: `1px solid ${C.border}`,
                                            color: C.muted,
                                            flexShrink: 0,
                                        }}>
                                        <span>Item</span>
                                        <span style={{ textAlign: "right", paddingRight: 12 }}>Qty</span>
                                        <span style={{ textAlign: "right" }}>Lost</span>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        {paginatedItems.map((item, i) => {
                                            const globalIndex = (itemPage - 1) * ITEMS_PAGE_SIZE + i
                                            const isTop = globalIndex === 0
                                            const barWidth = Math.round((item.count / maxCount) * 100)
                                            return (
                                                <div key={item.name} className="relative px-3 py-2.5"
                                                    style={{
                                                        borderBottom: i < paginatedItems.length - 1 ? `1px solid ${C.border}` : "none",
                                                        background: isTop ? C.coral + "06" : "transparent",
                                                    }}>
                                                    <div style={{
                                                        position: "absolute", left: 0, top: 0, bottom: 0,
                                                        width: `${barWidth}%`,
                                                        background: isTop ? C.coral + "12" : C.amber + "08",
                                                        borderRight: `2px solid ${isTop ? C.coral : C.amber}22`,
                                                        pointerEvents: "none",
                                                    }} />
                                                    <div className="relative grid items-center gap-2"
                                                        style={{ gridTemplateColumns: "1fr auto auto" }}>
                                                        <div className="min-w-0">
                                                            <p className="m-0 text-[12px] font-semibold truncate"
                                                                style={{ color: isTop ? C.coral : C.text }}>
                                                                {isTop && <span style={{ marginRight: 4 }}>🔴</span>}
                                                                {item.name}
                                                            </p>
                                                            <p className="m-0 text-[10px]" style={{ color: C.muted }}>
                                                                {item.category}
                                                            </p>
                                                        </div>
                                                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                                                            style={{
                                                                background: isTop ? C.coral + "18" : C.border + "60",
                                                                color: isTop ? C.coral : C.textSoft,
                                                                minWidth: 28, textAlign: "center",
                                                            }}>
                                                            ×{item.count}
                                                        </span>
                                                        <span className="text-[11px] font-semibold"
                                                            style={{ color: C.purple, minWidth: 64, textAlign: "right" }}>
                                                            {fmt(item.lost)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>

                                    <div className="grid px-3 py-2 text-[11px] font-semibold"
                                        style={{
                                            gridTemplateColumns: "1fr auto auto",
                                            background: C.dark,
                                            borderTop: `1px solid ${C.border}`,
                                            color: C.text,
                                            flexShrink: 0,
                                        }}>
                                        <span style={{ color: C.muted }}>All {byItem.length} items total</span>
                                        <span style={{ textAlign: "right", paddingRight: 12, color: C.coral }}>
                                            ×{byItem.reduce((s, i) => s + i.count, 0)}
                                        </span>
                                        <span style={{ textAlign: "right", color: C.purple }}>
                                            {fmt(byItem.reduce((s, i) => s + i.lost, 0))}
                                        </span>
                                    </div>
                                </div>

                                {itemTotalPages > 1 && (
                                    <div className="flex items-center justify-between pt-1" style={{ flexShrink: 0 }}>
                                        <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                            {(itemPage - 1) * ITEMS_PAGE_SIZE + 1}–{Math.min(itemPage * ITEMS_PAGE_SIZE, byItem.length)} of {byItem.length}
                                        </p>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setItemPage(p => Math.max(1, p - 1))} disabled={itemPage === 1}
                                                style={{
                                                    padding: "3px 10px", borderRadius: 8,
                                                    border: `1px solid ${C.border}`, background: C.card,
                                                    color: itemPage === 1 ? C.border : C.textSoft,
                                                    fontSize: 11, cursor: itemPage === 1 ? "not-allowed" : "pointer",
                                                }}>← Prev</button>
                                            <span style={{ fontSize: 11, color: C.textSoft, fontWeight: 600, minWidth: 48, textAlign: "center" }}>
                                                {itemPage} of {itemTotalPages}
                                            </span>
                                            <button onClick={() => setItemPage(p => Math.min(itemTotalPages, p + 1))} disabled={itemPage === itemTotalPages}
                                                style={{
                                                    padding: "3px 10px", borderRadius: 8,
                                                    border: `1px solid ${C.border}`, background: C.card,
                                                    color: itemPage === itemTotalPages ? C.border : C.textSoft,
                                                    fontSize: 11, cursor: itemPage === itemTotalPages ? "not-allowed" : "pointer",
                                                }}>Next →</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    )
}

const RemakeRequestsCard = ({ data, loading }) => {
    const empty = !hasData(data)
    const totalRemakes = empty ? 0 : data.length

    const byDay = useMemo(() => {
        if (empty) return []
        const map = {}
        data.forEach(r => {
            const key = fmtDate(toPHT(r.created_at))
            map[key] = (map[key] || 0) + 1
        })
        return Object.entries(map).map(([date, count]) => ({ date, count }))
    }, [data, empty])

    const byReason = empty ? [] : Object.entries(
        data.reduce((acc, r) => {
            const key = r.reason || "Unspecified"
            acc[key] = (acc[key] || 0) + 1
            return acc
        }, {})
    ).map(([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count)

    return (
        <Card>
            <SectionTitle title="Remake Requests" subtitle="Quality issues flagged for remake — your branch only" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Remakes" /><EmptyKPI label="Top Reason" /></>
                ) : (
                    <>
                        <KPI label="Total Remakes" value={totalRemakes.toLocaleString()} color={C.coral} />
                        <KPI label="Top Reason" value={byReason[0]?.reason ?? "—"}
                            sub={byReason[0] ? `${byReason[0].count} occurrence${byReason[0].count !== 1 ? "s" : ""}` : ""} color={C.purple} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={220} /> : empty ? (
                <EmptyState message="No remake requests for this period." height={220} />
            ) : (
                <ResponsiveContainer debounce={200} width="100%" height={220}>
                    <BarChart data={byDay} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                        <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: C.textSoft, fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: C.muted, fontSize: 10 }} allowDecimals={false} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                        <Bar dataKey="count" name="Remakes" fill={C.coral} radius={[5, 5, 0, 0]} opacity={0.85} />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </Card>
    )
}

const InventoryLoss = ({ data, loading }) => {
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 5
    const empty = !hasData(data)

    const totalLoss = empty ? 0 : data
        .filter(d => d.discrepancy < 0)
        .reduce((s, d) => s + Math.abs(Number(d.discrepancy)), 0)

    const totalSurplus = empty ? 0 : data
        .filter(d => d.discrepancy > 0)
        .reduce((s, d) => s + Number(d.discrepancy), 0)

    const getUnit = (row) => {
        const m = row.notes?.match(/(\w+)\.$/)
        return m ? m[1] : ""
    }

    const groupedByDate = useMemo(() => {
        if (empty) return []
        const map = {}
        for (const row of data) {
            const dateKey = new Date(row.created_at).toLocaleDateString("en-PH", {
                month: "short", day: "numeric", year: "numeric", timeZone: "Asia/Manila"
            })
            if (!map[dateKey]) map[dateKey] = []
            map[dateKey].push(row)
        }
        return Object.entries(map).sort((a, b) => new Date(b[0]) - new Date(a[0]))
    }, [data, empty])

    const allSortedRows = useMemo(() => {
        return groupedByDate.flatMap(([dateLabel, rows]) =>
            [...rows]
                .sort((a, b) => a.discrepancy - b.discrepancy)
                .map(row => ({ ...row, _dateLabel: dateLabel }))
        )
    }, [groupedByDate])

    const totalPages = Math.ceil(allSortedRows.length / PAGE_SIZE)
    const paginatedRows = allSortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    const paginatedGroups = useMemo(() => {
        const map = {}
        for (const row of paginatedRows) {
            if (!map[row._dateLabel]) map[row._dateLabel] = []
            map[row._dateLabel].push(row)
        }
        return groupedByDate
            .filter(([label]) => map[label])
            .map(([label]) => [label, map[label]])
    }, [paginatedRows, groupedByDate])

    return (
        <Card>
            <div className="flex items-center justify-between mb-5">
                <SectionTitle
                    title="Inventory Variance"
                    subtitle="Staff count vs. actual stock discrepancies — your branch only"
                />
                <span className="text-[10px] px-2 py-1 rounded-full border"
                    style={{ borderColor: C.border, color: C.muted, background: C.dark }}>
                    {groupedByDate.length > 0
                        ? `${groupedByDate.length} day${groupedByDate.length !== 1 ? "s" : ""} of data`
                        : new Date().toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })
                    }
                </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <>
                        <EmptyKPI label="Total Loss" />
                        <EmptyKPI label="Total Surplus" />
                        <EmptyKPI label="Items w/ Variance" />
                    </>
                ) : (
                    <>
                        <KPI label="Total Loss (units)" value={totalLoss.toLocaleString()} sub="negative discrepancy" color={C.coral} />
                        <KPI label="Total Surplus (units)" value={totalSurplus.toLocaleString()} sub="positive discrepancy" color={C.green} />
                        <KPI label="Items w/ Variance" value={data.filter(d => d.discrepancy !== 0).length} sub={`of ${data.length} counted`} color={C.amber} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={300} /> : empty ? (
                <EmptyState message="No inventory counts recorded for this period." height={200} />
            ) : (
                <div>
                    <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                        All Variance Records
                    </p>
                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
                        <div className="grid px-4 py-2 text-[10px] uppercase tracking-wider"
                            style={{
                                gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                background: C.dark,
                                borderBottom: `1px solid ${C.border}`,
                                color: C.muted,
                            }}>
                            <span>Notes / Time</span>
                            <span style={{ textAlign: "right" }}>Counted</span>
                            <span style={{ textAlign: "right" }}>Actual</span>
                            <span style={{ textAlign: "right" }}>Variance</span>
                        </div>

                        {paginatedGroups.map(([dateLabel, rows]) => {
                            const dayLoss = rows
                                .filter(r => r.discrepancy < 0)
                                .reduce((s, r) => s + Math.abs(Number(r.discrepancy)), 0)

                            return (
                                <React.Fragment key={dateLabel}>
                                    <div className="grid px-4 py-1.5"
                                        style={{
                                            gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                            background: C.amber + "10",
                                            borderBottom: `1px solid ${C.amber}22`,
                                            borderTop: `1px solid ${C.border}`,
                                        }}>
                                        <span style={{ fontSize: 10, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                            {dateLabel}
                                        </span>
                                        <span /><span />
                                        <span style={{ fontSize: 10, fontWeight: 600, color: C.coral, textAlign: "right" }}>
                                            {dayLoss > 0 ? `−${dayLoss.toLocaleString()} net loss` : ""}
                                        </span>
                                    </div>

                                    {rows.map((row, i) => {
                                        const isLoss = row.discrepancy < 0
                                        const isSurplus = row.discrepancy > 0
                                        const color = isLoss ? C.coral : isSurplus ? C.green : C.muted
                                        const unit = getUnit(row)
                                        return (
                                            <div key={row.staff_count_id}
                                                className="grid items-center px-4 py-2.5"
                                                style={{
                                                    gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                                    borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : "none",
                                                    background: isLoss ? C.coral + "06" : isSurplus ? C.green + "06" : "transparent",
                                                }}>
                                                <div className="min-w-0">
                                                    <p className="m-0 text-[11px] truncate" style={{ color: C.textSoft }}>
                                                        {row.notes ?? row.ingredient_id?.slice(0, 8)}
                                                    </p>
                                                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>
                                                        {new Date(row.created_at).toLocaleTimeString("en-PH", {
                                                            hour: "2-digit", minute: "2-digit", timeZone: "Asia/Manila"
                                                        })}
                                                    </p>
                                                </div>
                                                <span className="text-right text-[11px]" style={{ color: C.text }}>
                                                    {Number(row.counted_quantity).toLocaleString()} {unit}
                                                </span>
                                                <span className="text-right text-[11px]" style={{ color: C.text }}>
                                                    {Number(row.actual_quantity).toLocaleString()} {unit}
                                                </span>
                                                <span className="text-right text-[12px] font-bold" style={{ color }}>
                                                    {row.discrepancy > 0 ? "+" : ""}{Number(row.discrepancy).toLocaleString()} {unit}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </React.Fragment>
                            )
                        })}

                        <div className="grid px-4 py-2 text-[11px] font-semibold"
                            style={{
                                gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                background: C.dark,
                                borderTop: `1px solid ${C.border}`,
                            }}>
                            <span style={{ color: C.muted }}>
                                {data.filter(d => d.discrepancy < 0).length} loss · {data.filter(d => d.discrepancy > 0).length} surplus · {groupedByDate.length} day{groupedByDate.length !== 1 ? "s" : ""}
                            </span>
                            <span /><span />
                            <span className="text-right" style={{ color: C.coral }}>
                                −{totalLoss.toLocaleString()} net
                            </span>
                        </div>
                    </div>

                    {totalPages > 1 && (
                        <div className="no-print flex items-center justify-between mt-3">
                            <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, allSortedRows.length)} of {allSortedRows.length} records
                            </p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    style={{
                                        padding: "3px 10px", borderRadius: 8,
                                        border: `1px solid ${C.border}`, background: C.card,
                                        color: page === 1 ? C.border : C.textSoft,
                                        fontSize: 11, cursor: page === 1 ? "not-allowed" : "pointer",
                                    }}>
                                    ← Prev
                                </button>
                                <span style={{ fontSize: 11, color: C.textSoft, fontWeight: 600, minWidth: 48, textAlign: "center" }}>
                                    {page} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    style={{
                                        padding: "3px 10px", borderRadius: 8,
                                        border: `1px solid ${C.border}`, background: C.card,
                                        color: page === totalPages ? C.border : C.textSoft,
                                        fontSize: 11, cursor: page === totalPages ? "not-allowed" : "pointer",
                                    }}>
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Card>
    )
}

const PULLOUT_COLORS = [C.purple, C.teal, C.blue, C.amber, C.coral]

const RewardsAndPullouts = ({ redemptions, pullouts, loading }) => {
    const redemptionsEmpty = !hasData(redemptions)
    const pulloutsEmpty = !hasData(pullouts)
    const allEmpty = redemptionsEmpty && pulloutsEmpty

    const totalRedemptions = redemptionsEmpty ? 0 : redemptions.length
    const totalRewardValue = redemptionsEmpty ? 0 : redemptions.reduce((s, r) => s + Number(r.rewards?.reward_value ?? 0), 0)

    const byReward = redemptionsEmpty ? [] : Object.entries(
        redemptions.reduce((acc, r) => {
            const name = r.rewards?.reward_name ?? "Unknown"
            if (!acc[name]) acc[name] = { name, count: 0, valueGiven: 0 }
            acc[name].count += 1
            acc[name].valueGiven += Number(r.rewards?.reward_value ?? 0)
            return acc
        }, {})
    ).map(([, v]) => v).sort((a, b) => b.count - a.count)

    const totalPulloutValue = pulloutsEmpty ? 0 : pullouts.reduce((s, p) => s + Number(p.transaction_amount ?? 0), 0)
    const totalPulloutCount = pulloutsEmpty ? 0 : pullouts.length
    const totalCost = totalRewardValue + totalPulloutValue

    const sortedPullouts = pulloutsEmpty ? [] : [...pullouts].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    return (
        <Card>
            <SectionTitle title="Rewards & Pullouts" subtitle="Loyalty redemptions and manager-authorized complimentary transactions" />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
                {allEmpty ? (
                    <>
                        <EmptyKPI label="Redemptions" />
                        <EmptyKPI label="Reward Value Given" />
                        <EmptyKPI label="Pullout Total" />
                        <EmptyKPI label="Total Promo Cost" />
                    </>
                ) : (
                    <>
                        <KPI label="Redemptions" value={totalRedemptions.toLocaleString()} sub="completed rewards" color={C.purple} />
                        <KPI label="Reward Value Given" value={fmt(totalRewardValue)} color={C.teal} />
                        <KPI label="Pullout Total" value={fmt(totalPulloutValue)}
                            sub={`${totalPulloutCount} transaction${totalPulloutCount !== 1 ? "s" : ""}`} color={C.coral} />
                        <KPI label="Total Promo Cost" value={fmt(totalCost)} sub="rewards + pullouts" color={C.amber} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={240} /> : allEmpty ? (
                <EmptyState message="No reward redemptions or pullout transactions for this period." height={240} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div>
                        <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                            Redemptions by Reward
                        </p>
                        {redemptionsEmpty ? (
                            <EmptyState message="No redemptions this period." height={180} />
                        ) : (
                            <div className="flex flex-col gap-1.5">
                                {byReward.map((r, i) => (
                                    <div key={r.name}
                                        className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                                        style={{ background: C.dark, border: `1px solid ${C.border}` }}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div style={{ width: 8, height: 8, borderRadius: "50%", background: PULLOUT_COLORS[i % PULLOUT_COLORS.length], flexShrink: 0 }} />
                                            <span className="truncate" style={{ fontSize: 12, color: C.text }}>{r.name}</span>
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            <span style={{ fontSize: 11, color: C.muted }}>×{r.count}</span>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: C.teal, minWidth: 64, textAlign: "right" }}>{fmt(r.valueGiven)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                            Marketing Pullout Transactions
                        </p>
                        {pulloutsEmpty ? (
                            <EmptyState message="No pullouts this period." height={180} />
                        ) : (
                            <div className="flex flex-col gap-1.5">
                                {sortedPullouts.slice(0, 5).map((p, i) => (
                                    <div key={p.order_transaction_id}
                                        className="flex items-center justify-between px-3 py-2 rounded-lg"
                                        style={{
                                            background: i === 0 ? C.coral + "08" : C.dark,
                                            border: `1px solid ${i === 0 ? C.coral + "33" : C.border}`,
                                        }}>
                                        <div className="flex items-center gap-2">
                                            <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.coral, flexShrink: 0 }} />
                                            <span style={{ fontSize: 11, color: C.textSoft }}>{fmtDate(toPHT(p.created_at))}</span>
                                        </div>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: C.coral }}>{fmt(p.transaction_amount)}</span>
                                    </div>
                                ))}
                                <div className="flex items-center justify-between px-3 py-1.5 rounded-lg mt-1"
                                    style={{ background: C.dark, border: `1px solid ${C.border}` }}>
                                    <span style={{ fontSize: 11, color: C.muted }}>Total pullout cost</span>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: C.coral }}>{fmt(totalPulloutValue)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    )
}

const PaymentMethodsCard = ({ data, loading }) => {
    const { methods, top, totalAmount, totalCount } = summarizePaymentMethods(data)
    const empty = methods.length === 0

    return (
        <Card>
            <SectionTitle title="Payment Methods" subtitle="How customers paid — your branch only" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Transactions" /><EmptyKPI label="Total Collected" /><EmptyKPI label="Most Used" /></>
                ) : (
                    <>
                        <KPI label="Total Transactions" value={totalCount.toLocaleString()} color={C.blue} />
                        <KPI label="Total Collected" value={fmt(totalAmount)} color={C.green} />
                        <KPI label="Most Used" value={formatPaymentLabel(top?.method)} sub={`${top?.count} transactions`} color={C.amber} />
                    </>
                )}
            </div>
            {loading ? <Spinner height={200} /> : empty ? (
                <EmptyState message="No payment transactions for this period." height={200} />
            ) : (
                <div className="flex flex-col gap-1.5">
                    {methods.map((m) => {
                        const pct = totalCount ? (m.count / totalCount) * 100 : 0
                        return (
                            <div key={m.method} className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                                style={{ background: C.dark, border: `1px solid ${C.border}` }}>
                                <span style={{ fontSize: 12, color: C.text }}>{formatPaymentLabel(m.method)}</span>
                                <div className="flex items-center gap-3">
                                    <span style={{ fontSize: 11, color: C.muted }}>{pct.toFixed(0)}%</span>
                                    <span style={{ fontSize: 11, color: C.muted }}>×{m.count}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: C.teal, minWidth: 64, textAlign: "right" }}>
                                        {fmt(m.amount)}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </Card>
    )
}

const parseAISections = (text) => {
  if (!text) return []
  const lines = text.split('\n')
  const sections = []
  let current = null

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue

    const isHeader = trimmed.startsWith('### ')
    if (isHeader) {
      if (current) sections.push(current)
      current = { title: trimmed.replace(/^###\s*/, ''), bullets: [] }
    } else if (/^[-•*]\s+/.test(trimmed)) {
      if (!current) current = { title: null, bullets: [] }
      current.bullets.push(trimmed.replace(/^[-•*]\s+/, ''))
    } else {
      if (!current) current = { title: null, bullets: [] }
      current.bullets.push(trimmed)
    }
  }
  if (current) sections.push(current)
  return sections
}

const SECTION_COLORS = {
    'SALES & REVENUE':     { color: C.green,  icon: '📈' },
    'INVENTORY VARIANCE':  { color: C.purple, icon: '📦' },
    'PEAK HOUR TRAFFIC':   { color: C.amber,  icon: '🕐' },
    'BRANCH PERFORMANCE':  { color: C.teal,   icon: '🏪' },
    'MARKETING ROI':       { color: AI,       icon: '📣' },
    'VOIDED ITEMS':        { color: C.coral,  icon: '🗑️' },
}

const DEFAULT_SECTION_COLORS = [AI, C.teal, C.amber, C.coral, C.green]

const renderBulletText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} style={{ fontWeight: 600, color: C.text }}>{part.slice(2, -2)}</strong>
      : part
  )
}

const MarkdownBlock = ({ text }) => {
  if (!text) return null
  const sections = parseAISections(text)
  if (!sections.length) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {sections.map((section, idx) => {
        const key = section.title?.toUpperCase().trim()
        const cfg = (key && SECTION_COLORS[key]) || {
          color: DEFAULT_SECTION_COLORS[idx % DEFAULT_SECTION_COLORS.length],
          icon: '•',
        }

        return (
          <div key={idx} style={{
            borderRadius: 10,
            border: `1px solid ${cfg.color}28`,
            overflow: 'hidden',
          }}>
            {section.title && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px',
                background: cfg.color + '10',
                borderBottom: `1px solid ${cfg.color}20`,
              }}>
                <span style={{ fontSize: 13 }}>{cfg.icon}</span>
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  color: cfg.color,
                }}>
                  {section.title}
                </span>
              </div>
            )}

            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {section.bullets.map((bullet, bIdx) => (
                <div key={bIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: cfg.color, flexShrink: 0, marginTop: 7,
                  }} />
                  <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>
                    {renderBulletText(bullet)}
                  </span>
                </div>
              ))}
              {section.bullets.length === 0 && (
                <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>
                  {section.title}
                </span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const AISidebar = ({ open, onClose, summary, recommendations, loading, error, onGenerate, hasData, generatedAt, generatedFor, history, historyLoading, onDeleteHistory }) => {
    const [activeTab, setActiveTab] = useState("analysis") // "analysis" | "history"
    const [expandedId, setExpandedId] = useState(null)
    const [deletingId, setDeletingId] = useState(null)

    const handleDelete = async (id) => {
        setDeletingId(id)
        await onDeleteHistory(id)
        if (expandedId === id) setExpandedId(null)
        setDeletingId(null)
    }

    return (
        <>
            {open && (
                <div onClick={onClose} style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.18)",
                    zIndex: 999,
                }} />
            )}

            <div style={{
                position: "fixed",
                top: 0, right: 0, bottom: 0,
                width: "min(420px, 100vw)",
                background: "#fff",
                borderLeft: `1px solid ${C.border}`,
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                transform: open ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
                boxShadow: open ? "-4px 0 24px rgba(0,0,0,0.10)" : "none",
            }}>
                {/* Header */}
                <div style={{
                    padding: "16px 20px",
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexShrink: 0,
                    background: AI + "06",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: "50%",
                            background: AI,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24">
                                <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                            </svg>
                        </div>
                        <div>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Reports & Analytics AI Analyst</p>
                            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Interpretation · Forecast · Outlook</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: "none", border: "none", cursor: "pointer",
                        padding: 6, borderRadius: 8, color: C.muted,
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                {/* Tab switcher */}
                <div style={{
                    display: "flex",
                    borderBottom: `1px solid ${C.border}`,
                    flexShrink: 0,
                    background: C.dark,
                }}>
                    {[
                        { id: "analysis", label: "Analysis" },
                        { id: "history",  label: `History${history?.length ? ` (${history.length})` : ""}` },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                flex: 1,
                                padding: "10px 0",
                                background: "none",
                                border: "none",
                                borderBottom: activeTab === tab.id ? `2px solid ${AI}` : "2px solid transparent",
                                color: activeTab === tab.id ? AI : C.muted,
                                fontSize: 12,
                                fontWeight: activeTab === tab.id ? 700 : 400,
                                cursor: "pointer",
                                transition: "all 0.15s",
                                marginBottom: -1,
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: "auto", padding: "20px", minHeight: 0 }}>
                    {activeTab === "analysis" && (
                        <>
                            {/* Generate / Regenerate button */}
                            <button
                                onClick={onGenerate}
                                disabled={loading || !hasData}
                                style={{
                                    width: "100%",
                                    padding: "10px 16px",
                                    borderRadius: 50,
                                    border: "none",
                                    background: loading || !hasData ? C.border : AI,
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: loading || !hasData ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    gap: 7,
                                    marginBottom: summary ? 8 : 20,
                                    transition: "background 0.2s",
                                }}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                                            <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                                            <path fill="#fff" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        Analyzing your data…
                                    </>
                                ) : summary ? (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                                            <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.62"/>
                                        </svg>
                                        Regenerate Analysis
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                            <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                                        </svg>
                                        Generate Analysis
                                    </>
                                )}
                            </button>

                            {/* Last generated badge */}
                            {summary && !loading && (
                                    <div style={{
                                        display: "flex", justifyContent: "center",
                                        gap: 6, marginBottom: 16, flexWrap: "wrap",
                                    }}>
                                        {[
                                            "🤖 Gemini 2.5 Flash",
                                            "📊 Based on your data",
                                            "⚠️ For reference only",
                                        ].map(label => (
                                            <span key={label} style={{
                                                fontSize: 9, padding: "2px 8px",
                                                borderRadius: 50, border: `1px solid ${C.border}`,
                                                color: C.muted, background: C.dark,
                                            }}>
                                                {label}
                                            </span>
                                        ))}
                                    </div>
                                )}

                            {/* AI DISCLAIMER */}
                            <div style={{
                                padding: "10px 14px", borderRadius: 10,
                                background: C.dark,
                                border: `1px solid ${C.border}`,
                                display: "flex", gap: 8, alignItems: "flex-start",
                            }}>
                                <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
                                <p style={{ margin: 0, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                                    <strong style={{ color: C.textSoft }}>AI-Generated Content.</strong>{" "}
                                    This analysis is produced by Google Gemini 2.5 Flash based on your
                                    branch's operational data. Insights are pattern-based interpretations, not
                                    verified findings. Validate against your own business judgment before
                                    making operational decisions. KaPerk does not guarantee
                                    accuracy of AI-generated outputs.
                                </p>
                            </div>

                            {error && (
                                <div style={{
                                    background: C.coral + "15", border: `1px solid ${C.coral}44`,
                                    borderRadius: 10, padding: "10px 14px",
                                    fontSize: 12, color: C.coral, marginBottom: 16,
                                }}>
                                    {error}
                                </div>
                            )}

                            {!summary && !loading && !error && (
                                <div style={{
                                    display: "flex", flexDirection: "column",
                                    alignItems: "center", justifyContent: "center",
                                    gap: 12, padding: "40px 20px", textAlign: "center",
                                }}>
                                    <div style={{
                                        width: 56, height: 56, borderRadius: "50%",
                                        background: AI + "12",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                                            <path fill={AI} opacity="0.7" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600, color: C.text }}>
                                            Data Interpretation & Forecast
                                        </p>
                                        <p style={{ margin: 0, fontSize: 12, color: C.textSoft, maxWidth: 260, lineHeight: 1.6 }}>
                                            Click <strong>Generate Analysis</strong> to get a plain-language reading of what your branch's data means and what it signals for the near future.
                                        </p>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", marginTop: 4 }}>
                                        {[
                                            { icon: "📊", label: "Revenue interpretation" },
                                            { icon: "🕐", label: "Traffic pattern reading" },
                                            { icon: "🔮", label: "Short-term outlook" },
                                            { icon: "⚠️", label: "Risk signals" },
                                        ].map(item => (
                                            <div key={item.label} style={{
                                                display: "flex", alignItems: "center", gap: 10,
                                                padding: "8px 12px", borderRadius: 8,
                                                background: C.dark, border: `1px solid ${C.border}`,
                                            }}>
                                                <span style={{ fontSize: 14 }}>{item.icon}</span>
                                                <span style={{ fontSize: 12, color: C.textSoft }}>{item.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {loading && (
                                <div style={{
                                    display: "flex", flexDirection: "column",
                                    alignItems: "center", gap: 12, padding: "40px 20px",
                                }}>
                                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 28, height: 28 }}>
                                        <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                        <path fill={AI} d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                    <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Reading your data…</p>
                                </div>
                            )}

                            {summary && !loading && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                    <div style={{
                                        background: AI + "08", border: `1px solid ${AI}28`,
                                        borderRadius: 12, overflow: "hidden",
                                    }}>
                                        <div style={{
                                            padding: "10px 14px", borderBottom: `1px solid ${AI}18`,
                                            background: AI + "10", display: "flex", alignItems: "center", gap: 8,
                                        }}>
                                            <span style={{ fontSize: 14 }}>📊</span>
                                            <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: AI }}>
                                                What the Data Shows
                                            </p>
                                        </div>
                                        <div style={{ padding: "12px 14px" }}>
                                            <MarkdownBlock text={summary} />
                                        </div>
                                    </div>

                                    {recommendations && (
                                        <div style={{
                                            background: C.teal + "08", border: `1px solid ${C.teal}28`,
                                            borderRadius: 12, overflow: "hidden",
                                        }}>
                                            <div style={{
                                                padding: "10px 14px", borderBottom: `1px solid ${C.teal}18`,
                                                background: C.teal + "10", display: "flex", alignItems: "center", gap: 8,
                                            }}>
                                                <span style={{ fontSize: 14 }}>🔮</span>
                                                <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.teal }}>
                                                    Outlook & What to Anticipate
                                                </p>
                                            </div>
                                            <div style={{ padding: "12px 14px" }}>
                                               <MarkdownBlock text={recommendations} />
                                            </div>
                                        </div>
                                    )}

                                    <div style={{
                                        padding: "10px 14px", borderRadius: 10,
                                        background: C.amber + "08", border: `1px solid ${C.amber}22`,
                                        display: "flex", gap: 8, alignItems: "flex-start",
                                    }}>
                                        <span style={{ fontSize: 13, flexShrink: 0 }}>ℹ️</span>
                                        <p style={{ margin: 0, fontSize: 11, color: C.textSoft, lineHeight: 1.6 }}>
                                            This analysis reflects the filters active when it was generated. If you've changed the date range, click <strong>Regenerate</strong> for a fresh read.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* HISTORY TAB */}
                    {activeTab === "history" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {historyLoading ? (
                                <Spinner height={160} />
                            ) : !history?.length ? (
                                <div style={{
                                    display: "flex", flexDirection: "column",
                                    alignItems: "center", gap: 10,
                                    padding: "40px 20px", textAlign: "center",
                                }}>
                                    <div style={{
                                        width: 48, height: 48, borderRadius: "50%",
                                        background: C.muted + "18",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 22,
                                    }}>🕓</div>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>No history yet</p>
                                    <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                                        Generated analyses will be saved here automatically.
                                    </p>
                                </div>
                            ) : history.map((entry) => {
                                const isOpen = expandedId === entry.id
                                const generatedDate = new Date(entry.generated_at)
                                return (
                                    <div
                                        key={entry.id}
                                        style={{
                                            borderRadius: 12,
                                            border: `1px solid ${isOpen ? AI + "44" : C.border}`,
                                            overflow: "hidden",
                                            transition: "border-color 0.15s",
                                        }}
                                    >
                                        {/* Entry header — always visible */}
                                        <div
                                            style={{
                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                padding: "10px 14px",
                                                background: isOpen ? AI + "08" : C.dark,
                                                cursor: "pointer",
                                                gap: 10,
                                                transition: "background 0.15s",
                                            }}
                                            onClick={() => setExpandedId(isOpen ? null : entry.id)}
                                        >
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 700, color: AI,
                                                        background: AI + "15", borderRadius: 50,
                                                        padding: "2px 8px",
                                                    }}>
                                                        {entry.branch_name || "Your Branch"}
                                                    </span>
                                                    <span style={{
                                                        fontSize: 10, color: C.textSoft,
                                                        background: C.border + "80", borderRadius: 50,
                                                        padding: "2px 8px",
                                                    }}>
                                                        {entry.date_range}
                                                    </span>
                                                </div>
                                                <p style={{ margin: "5px 0 0", fontSize: 11, color: C.muted }}>
                                                    {generatedDate.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
                                                    {" at "}
                                                    {generatedDate.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                                                </p>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                                {/* Delete button */}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(entry.id) }}
                                                    disabled={deletingId === entry.id}
                                                    title="Delete this entry"
                                                    style={{
                                                        background: "none", border: `1px solid ${C.border}`,
                                                        borderRadius: 6, padding: "3px 6px",
                                                        cursor: deletingId === entry.id ? "not-allowed" : "pointer",
                                                        color: C.muted, fontSize: 11,
                                                        display: "flex", alignItems: "center",
                                                        opacity: deletingId === entry.id ? 0.5 : 1,
                                                    }}
                                                >
                                                    {deletingId === entry.id ? (
                                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 11, height: 11 }}>
                                                            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4"/>
                                                            <path fill={C.muted} d="M4 12a8 8 0 018-8v8z"/>
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                                        </svg>
                                                    )}
                                                </button>
                                                {/* Chevron */}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                                    fill="none" stroke={isOpen ? AI : C.muted} strokeWidth="2"
                                                    style={{ transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                                                    <path d="M6 9l6 6 6-6"/>
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Expanded content */}
                                        {isOpen && (
                                            <div style={{ padding: "12px 14px", borderTop: `1px solid ${AI}18`, display: "flex", flexDirection: "column", gap: 10 }}>
                                                {/* Summary block */}
                                                <div style={{
                                                    background: AI + "06", borderRadius: 10,
                                                    border: `1px solid ${AI}18`, overflow: "hidden",
                                                }}>
                                                    <div style={{
                                                        padding: "7px 12px", borderBottom: `1px solid ${AI}14`,
                                                        background: AI + "0E",
                                                        display: "flex", alignItems: "center", gap: 6,
                                                    }}>
                                                        <span style={{ fontSize: 12 }}>📊</span>
                                                        <span style={{ fontSize: 10, fontWeight: 700, color: AI, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                                            What the Data Shows
                                                        </span>
                                                    </div>
                                                    <div style={{ padding: "10px 12px" }}>
                                                        <MarkdownBlock text={entry.summary} />
                                                    </div>
                                                </div>

                                                {/* Recommendations */}
                                                {entry.recommendations && (
                                                    <div style={{
                                                        background: C.teal + "06", borderRadius: 10,
                                                        border: `1px solid ${C.teal}18`, overflow: "hidden",
                                                    }}>
                                                        <div style={{
                                                            padding: "7px 12px", borderBottom: `1px solid ${C.teal}14`,
                                                            background: C.teal + "0E",
                                                            display: "flex", alignItems: "center", gap: 6,
                                                        }}>
                                                            <span style={{ fontSize: 12 }}>🔮</span>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                                                Outlook & What to Anticipate
                                                            </span>
                                                        </div>
                                                        <div style={{ padding: "10px 12px" }}>
                                                            <MarkdownBlock text={entry.recommendations} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

const AIFloatingButton = ({ onClick, hasNew }) => (
    <button
        onClick={onClick}
        title="Open AI Analyst"
        style={{
            position: "fixed",
            bottom: 28, right: 28,
            width: 52, height: 52,
            borderRadius: "50%",
            background: AI,
            border: "none",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 18px rgba(37,99,235,0.35)",
            zIndex: 998,
            transition: "transform 0.15s, box-shadow 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(37,99,235,0.45)" }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(37,99,235,0.35)" }}
    >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
        </svg>
        {hasNew && (
            <span style={{
                position: "absolute", top: 2, right: 2,
                width: 10, height: 10, borderRadius: "50%",
                background: C.amber, border: "2px solid #fff",
            }} />
        )}
    </button>
)

async function exportBranchToPDF({
    branchName, periodLabel, role,
    salesData, peakHoursData, marketingData, menuItems,
    voidedData, inventoryData, redemptionsData, pulloutsData,
    remakeData,
    aiSummary, aiRecommendations,
}) {
    const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement("script");
        s.src = src; s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
    });

    try {
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
    } catch (e) {
        console.error("Failed to load PDF libraries.", e);
        return;
    }

    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 12;

    const AMBER = [217, 119, 6];
    const TEAL = [13, 148, 136];
    const DARK = [17, 24, 39];
    const GREY = [107, 114, 128];
    const BLUE = [37, 99, 235];
    const CORAL = [220, 38, 38];
    const PURPLE = [124, 58, 237];

    const cleanText = (str) => {
        if (!str) return "";
        return String(str)
            .replace(/\*\*(.*?)\*\*/g, "$1") // Strip bold tags
            .replace(/[\u20B1₱]/g, "PHP ")   // Replace Peso symbol
            .replace(/±/g, "PHP ")          // Clean encoding artifacts
            .trim();
    };

    const fmtPeso = (n) => `PHP ${Number(n ?? 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const hasRows = (d) => Array.isArray(d) && d.length > 0;

    const drawnHeaderPages = new Set();

    // Fixed drawHeader: Preserves and restores active page state
    const drawHeader = (pageNum) => {
        const targetPage = pageNum || doc.internal.getNumberOfPages();
        if (drawnHeaderPages.has(targetPage)) return;
        drawnHeaderPages.add(targetPage);

        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setPage(targetPage);

        const isFirst = targetPage === 1;

        doc.setFillColor(...DARK);
        doc.rect(0, 0, pageW, 16, "F");

        doc.setFillColor(...AMBER);
        doc.rect(0, 0, pageW, 2, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(
            isFirst ? "Coffee Gus\u00b7to \u2014 Branch Analytics Report" : "Coffee Gus\u00b7to \u2014 Branch Analytics Report (cont.)",
            margin, 10.5
        );

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(209, 213, 219);
        const rightLabel = [branchName || "All Branches", periodLabel].filter(Boolean).join("  |  ");
        doc.text(rightLabel, pageW - margin, 10.5, { align: "right" });

        // Restore active page cursor
        doc.setPage(currentPage);
    };

    let y = 22;

    const addPage = () => {
        doc.addPage();
        y = 22;
        drawHeader(doc.internal.getNumberOfPages());
    };

    const ensureRoom = (needed) => {
        if (y + needed > pageH - 16) {
            addPage();
        }
    };

    const sectionTitle = (text, accentRgb = AMBER) => {
        ensureRoom(14);
        doc.setFillColor(...accentRgb);
        doc.rect(margin, y, 3, 5, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10.5);
        doc.setTextColor(...DARK);
        doc.text(text, margin + 5, y + 4);
        y += 8;
    };

    const tableDefaults = {
        margin: { top: 22, left: margin, right: margin, bottom: 16 },
        theme: "grid",
        styles: { font: "helvetica", fontSize: 8, cellPadding: 2, textColor: [55, 65, 81] },
        headStyles: { fontSize: 8.5, fontStyle: "bold", textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        didDrawPage: (data) => {
            drawHeader(data.pageNumber);
        },
    };

    drawHeader(1);

    // 1. KPI Summary Cards
    if (hasRows(salesData)) {
        const totalRevenue = salesData.reduce((s, d) => s + d.revenue, 0);
        const totalOrders = salesData.reduce((s, d) => s + d.orders, 0);
        const avgTicket = totalOrders > 0 ? totalRevenue / totalOrders : 0;

        ensureRoom(22);
        const cardW = (pageW - margin * 2 - 8) / 3;
        const cards = [
            { label: "Total Revenue", val: fmtPeso(totalRevenue), color: TEAL },
            { label: "Total Orders", val: totalOrders.toLocaleString("en-PH"), color: BLUE },
            { label: "Average Ticket", val: fmtPeso(avgTicket), color: AMBER },
        ];

        cards.forEach((card, idx) => {
            const x = margin + idx * (cardW + 4);
            doc.setFillColor(249, 250, 251);
            doc.setDrawColor(229, 231, 235);
            doc.roundedRect(x, y, cardW, 16, 1.5, 1.5, "FD");

            doc.setFillColor(...card.color);
            doc.rect(x, y, 2, 16, "F");

            doc.setFont("helvetica", "normal");
            doc.setFontSize(7.5);
            doc.setTextColor(...GREY);
            doc.text(card.label, x + 5, y + 5.5);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(9.5);
            doc.setTextColor(...DARK);
            doc.text(card.val, x + 5, y + 12);
        });

        y += 22;
    }

    // 2. Sales & Revenue
    if (hasRows(salesData)) {
        const totalRevenue = salesData.reduce((s, d) => s + d.revenue, 0);
        const totalOrders = salesData.reduce((s, d) => s + d.orders, 0);
        sectionTitle("Sales & Revenue", TEAL);
        doc.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Date", "Revenue", "Orders", "Avg Ticket"]],
            body: salesData.map(d => [
                d.date, fmtPeso(d.revenue), d.orders, fmtPeso(d.orders > 0 ? d.revenue / d.orders : 0)
            ]),
            foot: [["TOTAL", fmtPeso(totalRevenue), totalOrders, fmtPeso(totalOrders > 0 ? totalRevenue / totalOrders : 0)]],
            headStyles: { ...tableDefaults.headStyles, fillColor: TEAL },
            footStyles: { fillColor: [240, 253, 250], textColor: DARK, fontStyle: "bold" },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "center" }, 3: { halign: "right" } },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    // 3. Peak Hours
    if (hasRows(peakHoursData)) {
        const totalOrds = peakHoursData.reduce((s, d) => s + d.orders, 0);
        sectionTitle("Peak Hour Analytics", AMBER);
        doc.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Hour", "Orders", "Revenue", "% of Orders"]],
            body: peakHoursData.map(d => [
                d.hour, d.orders, fmtPeso(d.revenue),
                totalOrds > 0 ? `${((d.orders / totalOrds) * 100).toFixed(1)}%` : "0%"
            ]),
            headStyles: { ...tableDefaults.headStyles, fillColor: AMBER },
            columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    // 4. Marketing ROI
    if (hasRows(marketingData)) {
        const totalSpend = marketingData.reduce((s, d) => s + d.spend, 0);
        const totalRev = marketingData.reduce((s, d) => s + d.revenue, 0);
        sectionTitle("Marketing Performance & ROI", PURPLE);
        doc.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Channel", "Spend", "Revenue Driven", "ROI %", "Net Profit"]],
            body: marketingData.map(d => {
                const roi = d.spend > 0 ? ((d.revenue - d.spend) / d.spend) * 100 : 0;
                return [d.channel, fmtPeso(d.spend), fmtPeso(d.revenue), `${roi.toFixed(1)}%`, fmtPeso(d.revenue - d.spend)];
            }),
            foot: [["TOTAL", fmtPeso(totalSpend), fmtPeso(totalRev),
                totalSpend > 0 ? `${(((totalRev - totalSpend) / totalSpend) * 100).toFixed(1)}%` : "0%",
                fmtPeso(totalRev - totalSpend)]],
            headStyles: { ...tableDefaults.headStyles, fillColor: PURPLE },
            footStyles: { fillColor: [245, 243, 255], textColor: DARK, fontStyle: "bold" },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    // 5. Voided Items
    if (hasRows(voidedData)) {
        const REASON_LABELS = {
            order_cancelled: "Order Cancelled", wrong_item: "Wrong Item",
            customer_changed_mind: "Customer Changed Mind", other: "Other",
        };
        const normalize = (r) => (r ?? "other").trim().toLowerCase().replace(/\s+/g, "_") || "other";
        const totalLost = voidedData.reduce((s, d) => s + Number(d.item_price) * Number(d.quantity), 0);

        sectionTitle("Voided Items", CORAL);
        doc.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Item", "Category", "Qty", "Reason", "Value Lost"]],
            body: voidedData.map(d => [
                d.menu_items?.name ?? "Unknown", d.menu_items?.category ?? "—", d.quantity,
                REASON_LABELS[normalize(d.void_reason)] ?? d.void_reason ?? "Other",
                fmtPeso(Number(d.item_price) * Number(d.quantity)),
            ]),
            foot: [["TOTAL", "", voidedData.reduce((s, d) => s + Number(d.quantity), 0), "", fmtPeso(totalLost)]],
            headStyles: { ...tableDefaults.headStyles, fillColor: CORAL },
            footStyles: { fillColor: [254, 242, 242], textColor: DARK, fontStyle: "bold" },
            columnStyles: { 2: { halign: "center" }, 4: { halign: "right" } },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    // 5b. Remake Requests
    if (hasRows(remakeData)) {
        sectionTitle("Remake Requests", CORAL);
        doc.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Reason", "Status", "Date"]],
            body: remakeData.map(r => [
                r.reason || "Unspecified",
                r.status || "—",
                new Date(r.created_at).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric", timeZone: "Asia/Manila" }),
            ]),
            foot: [[`TOTAL: ${remakeData.length} remake${remakeData.length !== 1 ? "s" : ""}`, "", ""]],
            headStyles: { ...tableDefaults.headStyles, fillColor: CORAL },
            footStyles: { fillColor: [254, 242, 242], textColor: DARK, fontStyle: "bold" },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    // 6. Inventory Variance
    if (hasRows(inventoryData)) {
        const totalLoss = inventoryData.filter(d => d.discrepancy < 0).reduce((s, d) => s + Math.abs(Number(d.discrepancy)), 0);
        sectionTitle("Inventory Variance", DARK);
        doc.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Notes / Item", "Counted", "Actual", "Variance", "Date"]],
            body: inventoryData.map(d => [
                d.notes ?? (d.ingredient_id?.slice(0, 8) ?? "—"),
                Number(d.counted_quantity).toLocaleString(),
                Number(d.actual_quantity).toLocaleString(),
                `${d.discrepancy > 0 ? "+" : ""}${Number(d.discrepancy).toLocaleString()}`,
                new Date(d.created_at).toLocaleDateString("en-PH", { month: "short", day: "numeric", timeZone: "Asia/Manila" }),
            ]),
            foot: [["Net Discrepancy Loss", "", "", `-${totalLoss.toLocaleString()}`, ""]],
            headStyles: { ...tableDefaults.headStyles, fillColor: DARK },
            footStyles: { fillColor: [254, 242, 242], textColor: CORAL, fontStyle: "bold" },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    // 7. Rewards & Pullouts
    if (hasRows(redemptionsData) || hasRows(pulloutsData)) {
        sectionTitle("Rewards & Pullouts", PURPLE);

        if (hasRows(redemptionsData)) {
            const byReward = Object.entries(
                redemptionsData.reduce((acc, r) => {
                    const name = r.rewards?.reward_name ?? "Unknown";
                    if (!acc[name]) acc[name] = { count: 0, value: 0 };
                    acc[name].count++;
                    acc[name].value += Number(r.rewards?.reward_value ?? 0);
                    return acc;
                }, {})
            );
            doc.autoTable({
                ...tableDefaults,
                startY: y,
                head: [["Reward Name", "Redemptions", "Value Given"]],
                body: byReward.map(([name, v]) => [name, v.count, fmtPeso(v.value)]),
                headStyles: { ...tableDefaults.headStyles, fillColor: PURPLE },
                columnStyles: { 1: { halign: "center" }, 2: { halign: "right" } },
            });
            y = doc.lastAutoTable.finalY + 6;
        }

        if (hasRows(pulloutsData)) {
            const totalPullout = pulloutsData.reduce((s, p) => s + Number(p.transaction_amount ?? 0), 0);
            ensureRoom(20);
            doc.autoTable({
                ...tableDefaults,
                startY: y,
                head: [["Pullout Date", "Amount"]],
                body: [...pulloutsData]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map(p => [
                        new Date(p.created_at).toLocaleDateString("en-PH", { month: "short", day: "numeric", timeZone: "Asia/Manila" }),
                        fmtPeso(p.transaction_amount),
                    ]),
                foot: [["TOTAL PULLOUTS", fmtPeso(totalPullout)]],
                headStyles: { ...tableDefaults.headStyles, fillColor: CORAL },
                footStyles: { fillColor: [254, 242, 242], textColor: DARK, fontStyle: "bold" },
                columnStyles: { 1: { halign: "right" } },
            });
            y = doc.lastAutoTable.finalY + 8;
        }
    }

    // 8. Menu Catalog Overview
    if (hasRows(menuItems)) {
        sectionTitle("Menu Catalog Overview", AMBER);
        doc.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Item Name", "Category", "Classification", "Price"]],
            body: menuItems.map(d => [d.name, d.category, d.classification, fmtPeso(d.item_price)]),
            headStyles: { ...tableDefaults.headStyles, fillColor: [146, 64, 14] },
            columnStyles: { 3: { halign: "right" } },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    // 9. AI Analysis Native Renderer (Bulletproof positioning & text formatting)
    const renderAISection = (title, rawText, accentRgb) => {
        if (!rawText) return;

        // Ensure Y coordinate aligns after the last table
        if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
            y = Math.max(y, doc.lastAutoTable.finalY + 8);
        }

        // Always push AI Analysis to a clean page if close to bottom
        if (y > pageH - 35) {
            addPage();
        }

        sectionTitle(title, accentRgb);

        const cleanedText = cleanText(rawText);
        const lines = cleanedText.split("\n").map(l => l.trim()).filter(Boolean);

        const contentWidth = pageW - margin * 2;
        const bulletIndent = 6;
        const textWidth = contentWidth - bulletIndent;

        for (const line of lines) {
            let isHeader = false;
            let headerText = "";
            let bodyText = "";

            // Check for Markdown / Inline Headers
            if (/^#{1,6}\s+/.test(line)) {
                isHeader = true;
                headerText = line.replace(/^#{1,6}\s+/, "");
            } else if (line.includes(" - ") && line.length < 100 && !line.startsWith("http")) {
                const parts = line.split(" - ");
                headerText = parts[0].trim();
                bodyText = parts.slice(1).join(" - ").trim();
            } else if (/^[A-Z\s]{4,30}$/.test(line)) {
                isHeader = true;
                headerText = line;
            }

            // Draw Section Subheaders
            if (isHeader || headerText) {
                ensureRoom(10);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor(...accentRgb);
                doc.text(headerText, margin + 2, y);
                y += 5;
                if (!bodyText) continue;
            }

            // Format Bullet Content
            const cleanBullet = (bodyText || line)
                .replace(/^[-•*]\s+/, "")
                .replace(/^\d+[\.\)]\s+/, "");

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8.5);

            const wrappedLines = doc.splitTextToSize(cleanBullet, textWidth);
            const blockHeight = wrappedLines.length * 4.2 + 2;

            ensureRoom(blockHeight);

            // Draw Accent Bullet Dot
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...accentRgb);
            doc.text("\u2022", margin + 1, y);

            // Draw Wrapped Text
            doc.setFont("helvetica", "normal");
            doc.setTextColor(55, 65, 81);
            doc.text(wrappedLines, margin + bulletIndent, y);

            y += wrappedLines.length * 4.2 + 2;
        }

        y += 6;
    };

    if (aiSummary) renderAISection("AI Analysis — Executive Summary", aiSummary, BLUE);
    if (aiRecommendations) renderAISection("AI Analysis — Recommendations & Outlook", aiRecommendations, TEAL);

    // Footer Branding & Page Numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...GREY);
        doc.text("Coffee Gus\u00b7to \u2014 Confidential Branch Analytics", margin, pageH - 7);
        doc.text(`Page ${i} of ${pageCount}`, pageW - margin, pageH - 7, { align: "right" });
    }

    const date = new Date().toISOString().split("T")[0];
    const branchSlug = branchName ? `_${branchName.replace(/\s+/g, "_")}` : "";
    doc.save(`CoffeeGusto_BranchReport${branchSlug}_${date}.pdf`);
}


export default function Analytics({ staffRole, branchId }) {
    const [range, setRange] = useState("30d")
    const [customStart, setCustomStart] = useState("")
    const [customEnd, setCustomEnd] = useState("")

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('staffDashboardFilterHandoff')
            if (raw) {
                const handoff = JSON.parse(raw)
                if (handoff.customStart && handoff.customEnd) {
                    setCustomStart(handoff.customStart)
                    setCustomEnd(handoff.customEnd)
                }
                sessionStorage.removeItem('staffDashboardFilterHandoff')
            }
        } catch (err) {
            console.error('Failed to apply staff dashboard filter handoff:', err)
        }
    }, [])

    const normalizedRole = staffRole?.toLowerCase().replace(/\s+/g, '_')
    const role = ['branch_admin', 'admin', 'branch_manager', 'manager'].includes(normalizedRole)
        ? 'branch_admin'
        : 'staff'

    const perms = ROLE_PERMISSIONS[role] ?? ROLE_PERMISSIONS.staff
    const branchName = useBranchName(branchId)
    const menuItems = useMenuItems()

    const hasCustom = customStart && customEnd
    const startISO = useMemo(() => {
        if (hasCustom) return new Date(customStart).toISOString()
        return presetStart(range)
    }, [hasCustom, customStart, range])

    const endISO = useMemo(() => {
        if (hasCustom) return new Date(customEnd + "T23:59:59").toISOString()
        return null
    }, [hasCustom, customEnd])

    const salesData = useSalesData(startISO, endISO, branchId, perms.showSalesRevenue)
    const peakHoursData = usePeakHoursData(startISO, endISO, branchId, perms.showPeakHours)
    const marketingData = useMarketing(startISO, endISO, branchId, perms.showMarketingROI)
    const voidedData  = useVoidedItems(startISO, endISO, branchId, perms.showVoidedItems)
    const voidLoading = voidedData === null && perms.showVoidedItems
    const paymentMethodsData = usePaymentMethods(startISO, endISO, branchId, perms.showPaymentMethods)
    const paymentMethodsLoading = paymentMethodsData === null && perms.showPaymentMethods   
    const paymentStats = useMemo(
        () => summarizePaymentMethods(paymentMethodsData ?? []),
        [paymentMethodsData]
    )
    const inventoryData = useInventoryDiscrepancies(startISO, endISO, branchId, perms.showInventoryVariance)
    const inventoryLoading = inventoryData === null && perms.showInventoryVariance
    const remakeData = useRemakeRequests(startISO, endISO, branchId, perms.showRemakeRequests)
    const remakeLoading = remakeData === null && perms.showRemakeRequests
    const { redemptions: redemptionsData, pullouts: pulloutsData } = useRewardsAndPullouts(startISO, endISO, branchId, perms.showRewardsPullouts)
    const rewardsLoading = perms.showRewardsPullouts && (redemptionsData === null || pulloutsData === null)

    const salesLoading = salesData === null && perms.showSalesRevenue
    const peakLoading = peakHoursData === null && perms.showPeakHours
    const mktLoading = marketingData === null && perms.showMarketingROI
    const menuLoading = menuItems === null

    const [aiSummary, setAiSummary] = useState(
        () => sessionStorage.getItem('branch_ra_aiSummary') || null
    )
    const [aiRecommendations, setAiRecommendations] = useState(
        () => sessionStorage.getItem('branch_ra_aiRecommendations') || null
    )
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState(null)
    const [aiSidebarOpen, setAiSidebarOpen] = useState(false)
    const [aiGeneratedAt, setAiGeneratedAt] = useState(null)
    const [aiGeneratedFor, setAiGeneratedFor] = useState(null)

    const { history, loading: historyLoading, saveAnalysis, deleteAnalysis } = useBranchAIHistory(branchId)

    const dashboardRef = useRef(null)
    const [showDownloadMenu, setShowDownloadMenu] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [pdfStatus, setPdfStatus] = useState("")
    const menuRef = useRef(null)

    const handleGenerateInsights = async () => {
        setAiLoading(true)
        setAiError(null)
        setAiSummary(null)
        setAiRecommendations(null)

        try {
            const totalRevenue = hasData(salesData) ? salesData.reduce((s, d) => s + d.revenue, 0) : 0
            const totalOrders = hasData(salesData) ? salesData.reduce((s, d) => s + d.orders, 0) : 0
            const avgTicket = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0
            const growth = hasData(salesData) && salesData.length >= 2
                ? (((salesData[salesData.length - 1].revenue - salesData[0].revenue) / (salesData[0].revenue || 1)) * 100).toFixed(1)
                : 0

            const peakHour = hasData(peakHoursData)
                ? peakHoursData.reduce((a, b) => a.orders >= b.orders ? a : b)
                : null

            const totalVoids = hasData(voidedData) ? voidedData.length : 0
            const revenueLost = hasData(voidedData)
                ? voidedData.reduce((s, d) => s + Number(d.item_price) * Number(d.quantity), 0)
                : 0
            const topReason = hasData(voidedData)
                ? Object.entries(
                    voidedData.reduce((acc, d) => { const k = d.void_reason ?? 'other'; acc[k] = (acc[k] || 0) + 1; return acc }, {})
                ).sort((a, b) => b[1] - a[1])[0]?.[0]
                : null

            const marketingPayload = hasData(marketingData) ? {
                channels: marketingData.map(d => ({
                    channel: d.channel,
                    spend: d.spend.toFixed(2),
                    revenue: d.revenue.toFixed(2),
                    roi: d.spend > 0 ? (((d.revenue - d.spend) / d.spend) * 100).toFixed(1) : "0.0",
                })),
                bestChannel: marketingData.reduce((best, ch) => {
                    const bestRoi = best.spend > 0 ? (best.revenue - best.spend) / best.spend : -Infinity
                    const chRoi = ch.spend > 0 ? (ch.revenue - ch.spend) / ch.spend : -Infinity
                    return chRoi > bestRoi ? ch : best
                }, marketingData[0]).channel,
            } : null

            const payload = {
                dateRange: { start: startISO, end: endISO || new Date().toISOString() },
                selectedBranchName: branchName,
                salesData: { totalRevenue, totalOrders, avgTicket, growth },
                peakHoursData: peakHour
                    ? { peakHour: peakHour.hour, peakOrders: peakHour.orders, totalOrders: hasData(peakHoursData) ? peakHoursData.reduce((s, d) => s + d.orders, 0) : 0 }
                    : null,
                marketingData: marketingPayload,
                voidedData: totalVoids > 0 ? { totalVoids, revenueLost, topReason } : null,
            }

            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${BASE_URL}/api/summarize-reports`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ ...payload, mode: "interpret_and_forecast" })
            })
            if (!res.ok) throw new Error('Failed to generate insights')
            const { summary, recommendations } = await res.json()
            setAiSummary(summary)
            setAiRecommendations(recommendations)
            setAiGeneratedAt(new Date())
            setAiGeneratedFor({
                branch: branchName || "Your Branch",
                range: hasCustom ? `${customStart} → ${customEnd}` : range,
            })
            await saveAnalysis({
                summary,
                recommendations,
                branchName: branchName || "Your Branch",
                dateRange: hasCustom ? `${customStart} → ${customEnd}` : range,
                startISO,
                endISO,
            })
            return { summary, recommendations }
        } catch (err) {
            setAiError(err.message || 'Failed to generate analysis. Please try again.')
            return null
        } finally {
            setAiLoading(false)
        }
    }

    const handleDownloadPDF = async () => {
        setPdfLoading(true)
        setPdfStatus("generating_ai")

        let summaryText = aiSummary
        let recText = aiRecommendations

        try {
            if (!summaryText) {
                const result = await handleGenerateInsights()
                if (!result) throw new Error("AI analysis failed — cannot generate PDF")
                summaryText = result?.summary ?? ""
                recText = result?.recommendations ?? ""
            }

            setPdfStatus("rendering")

            const periodStart = startISO.slice(0, 10)
            const periodEnd = endISO ? endISO.slice(0, 10) : new Date().toISOString().slice(0, 10)

            await exportBranchToPDF({
                branchName: branchName || "Your Branch",
                periodLabel: `${periodStart} to ${periodEnd}`,
                role,
                salesData: salesData ?? [],
                peakHoursData: peakHoursData ?? [],
                marketingData: marketingData ?? [],
                menuItems: menuItems ?? [],
                voidedData: voidedData ?? [],
                inventoryData: inventoryData ?? [],
                redemptionsData: redemptionsData ?? [],
                pulloutsData: pulloutsData ?? [],
                remakeData: remakeData ?? [],
                aiSummary: summaryText,
                aiRecommendations: recText,
            })
        } catch (err) {
            console.error("PDF export failed:", err)
            alert(err.message || "PDF export failed. Please try again.")
        } finally {
            setPdfLoading(false)
            setPdfStatus("")
        }
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowDownloadMenu(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])


   
    const handleExport = async () => {
        if (!window.ExcelJS) {
            await new Promise((resolve, reject) => {
                const script = document.createElement("script")
                script.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js"
                script.onload = resolve
                script.onerror = reject
                document.head.appendChild(script)
            })
        }

        const wb = new window.ExcelJS.Workbook()
        wb.creator = "Coffee Gus·to"
        wb.created = new Date()

        const BRAND_AMBER = "FFD97706"
        const BRAND_TEAL = "FF0D9488"
        const BRAND_GREEN = "FF16A34A"
        const BRAND_BLUE = "FF2563EB"
        const BRAND_CORAL = "FFDC2626"
        const BRAND_PURPLE = "FF7C3AED"
        const HEADER_BG = "FF111827"
        const ALT_ROW_BG = "FFF9FAFB"
        const TOTAL_BG = "FFFEF3C7"
        const WHITE = "FFFFFFFF"

        const headerStyle = (bgColor = HEADER_BG) => ({
            font: { name: "Arial", bold: true, color: { argb: WHITE }, size: 10 },
            fill: { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } },
            alignment: { horizontal: "center", vertical: "middle", wrapText: true },
            border: { bottom: { style: "medium", color: { argb: BRAND_AMBER } } },
        })

        const dataStyle = (numFmt, color = "FF111827", bold = false) => ({
            font: { name: "Arial", size: 10, color: { argb: color }, bold },
            alignment: { horizontal: "right", vertical: "middle" },
            numFmt,
        })

        const labelStyle = {
            font: { name: "Arial", size: 10, color: { argb: "FF374151" } },
            alignment: { horizontal: "left", vertical: "middle" },
        }

        const totalRowStyle = (numFmt) => ({
            font: { name: "Arial", bold: true, size: 10, color: { argb: "FF92400E" } },
            fill: { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_BG } },
            alignment: { horizontal: "right", vertical: "middle" },
            border: { top: { style: "thin", color: { argb: BRAND_AMBER } } },
            numFmt,
        })

        const addSheetTitle = (ws, title, subtitle, colCount) => {
            ws.mergeCells(1, 1, 1, colCount)
            const titleCell = ws.getCell("A1")
            titleCell.value = `Coffee Gus·to — ${title}`
            titleCell.font = { name: "Arial", bold: true, size: 14, color: { argb: WHITE } }
            titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_BG } }
            titleCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 }
            ws.getRow(1).height = 32

            ws.mergeCells(2, 1, 2, colCount)
            const subCell = ws.getCell("A2")
            subCell.value = subtitle
            subCell.font = { name: "Arial", italic: true, size: 9, color: { argb: "FF6B7280" } }
            subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } }
            subCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 }
            ws.getRow(2).height = 18
        }

        const applyAltRows = (ws, startRow, endRow, colCount, skipCols = []) => {
            for (let r = startRow; r <= endRow; r++) {
                if (r % 2 === 0) {
                    for (let c = 1; c <= colCount; c++) {
                        if (skipCols.includes(c)) continue
                        ws.getCell(r, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALT_ROW_BG } }
                    }
                }
            }
        }

        const shortBranch = branchName?.replace("Coffee Gus·to ", "") ?? "Branch"
        const exportLabel = `_${shortBranch}`
        const exportDate  = new Date().toISOString().split("T")[0]

        // SUMMARY SHEET
        const summaryWs = wb.addWorksheet("Summary")
        summaryWs.columns = [{ width: 28 }, { width: 22 }, { width: 22 }, { width: 22 }]
        addSheetTitle(summaryWs, "Report Summary", `Exported: ${exportDate} · Branch: ${shortBranch} · Role: ${role}`, 4)
        summaryWs.getRow(3).height = 6

        const hRow = summaryWs.getRow(4)
        hRow.values = ["Section", "Metric", "Value", "Notes"]
        hRow.height = 22
        ;[1, 2, 3, 4].forEach(ci => Object.assign(hRow.getCell(ci), headerStyle()))

        const summaryRows = []
        if (hasData(salesData)) {
            const rev = salesData.reduce((s, d) => s + d.revenue, 0)
            const ord = salesData.reduce((s, d) => s + d.orders,  0)
            summaryRows.push(["Sales & Revenue", "Total Revenue", rev, "₱ format"])
            summaryRows.push(["Sales & Revenue", "Total Orders", ord, "count"])
            summaryRows.push(["Sales & Revenue", "Avg Ticket", ord > 0 ? rev / ord : 0,"₱ format"])
        }
        if (hasData(peakHoursData)) {
            const peak = peakHoursData.reduce((a, b) => a.orders >= b.orders ? a : b)
            summaryRows.push(["Peak Hours", "Busiest Hour", peak.hour, ""])
            summaryRows.push(["Peak Hours", "Peak Orders", peak.orders, "count"])
            summaryRows.push(["Peak Hours", "Peak Revenue",  peak.revenue, "₱ format"])
        }
        if (hasData(marketingData)) {
            const ts = marketingData.reduce((s, d) => s + d.spend,   0)
            const tr = marketingData.reduce((s, d) => s + d.revenue, 0)
            summaryRows.push(["Marketing ROI", "Total Spend", ts, "₱ format"])
            summaryRows.push(["Marketing ROI", "Total Revenue", tr, "₱ format"])
            summaryRows.push(["Marketing ROI", "Blended ROI", ts > 0 ? (tr - ts) / ts : 0,"0.0%"])
        }
        if (hasData(remakeData)) {
            summaryRows.push(["Remake Requests", "Total Remakes", remakeData.length, ""])
        }

        summaryRows.forEach((row, i) => {
            const r = summaryWs.getRow(5 + i)
            r.height = 20
            r.getCell(1).value = row[0]; Object.assign(r.getCell(1), labelStyle)
            r.getCell(2).value = row[1]; Object.assign(r.getCell(2), labelStyle)
            const isROI = row[3] === "0.0%"
            r.getCell(3).value = row[2]
            Object.assign(r.getCell(3), dataStyle(
                isROI ? "0.0%" : typeof row[2] === "number" && row[3] === "₱ format" ? "₱#,##0.00" : "#,##0",
                typeof row[2] === "number" ? BRAND_TEAL : "FF374151"
            ))
            r.getCell(4).value = row[3]
            Object.assign(r.getCell(4), { ...labelStyle, font: { ...labelStyle.font, color: { argb: "FF9CA3AF" }, italic: true, size: 9 } })
        })
        applyAltRows(summaryWs, 5, 4 + summaryRows.length, 4)

        // SALES & REVENUE (branch_admin only)
        if (hasData(salesData)) {
            const ws = wb.addWorksheet("Sales & Revenue")
            ws.columns = [{ width: 16 }, { width: 18 }, { width: 12 }, { width: 18 }]
            addSheetTitle(ws, "Sales & Revenue", "Daily revenue and order count", 4)

            const hr = ws.getRow(4)
            hr.values = ["Date", "Revenue (₱)", "Orders", "Avg Ticket (₱)"]
            hr.height = 22
            ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_TEAL)))

            salesData.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                r.getCell(1).value = d.date; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = d.revenue; Object.assign(r.getCell(2), dataStyle("₱#,##0.00", BRAND_TEAL))
                r.getCell(3).value = d.orders; Object.assign(r.getCell(3), dataStyle("#,##0", BRAND_BLUE))
                r.getCell(4).value = d.orders > 0 ? d.revenue / d.orders : 0
                Object.assign(r.getCell(4), dataStyle("₱#,##0.00", BRAND_AMBER))
            })

            const lastDataRow = 4 + salesData.length
            const totR = ws.getRow(lastDataRow + 1)
            totR.height = 22
            totR.getCell(1).value = "TOTAL"
            Object.assign(totR.getCell(1), { ...totalRowStyle(""), alignment: { horizontal: "left" } })
            totR.getCell(2).value = { formula: `=SUM(B5:B${lastDataRow})` }; Object.assign(totR.getCell(2), totalRowStyle("₱#,##0.00"))
            totR.getCell(3).value = { formula: `=SUM(C5:C${lastDataRow})` }; Object.assign(totR.getCell(3), totalRowStyle("#,##0"))
            totR.getCell(4).value = { formula: `=IFERROR(B${lastDataRow+1}/C${lastDataRow+1},0)` }; Object.assign(totR.getCell(4), totalRowStyle("₱#,##0.00"))

            applyAltRows(ws, 5, lastDataRow, 4)
        }

        // PEAK HOURS
        if (hasData(peakHoursData)) {
            const ws = wb.addWorksheet("Peak Hours")
            ws.columns = [{ width: 12 }, { width: 12 }, { width: 18 }, { width: 18 }]
            addSheetTitle(ws, "Peak Hour Analytics", "Hourly order volume and revenue (PHT)", 4)

            const hr = ws.getRow(4)
            hr.values = ["Hour", "Orders", "Revenue (₱)", "% of Total Orders"]
            hr.height = 22
            ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_AMBER)))

            const totalOrds = peakHoursData.reduce((s, d) => s + d.orders, 0)
            peakHoursData.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                r.getCell(1).value = d.hour; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = d.orders; Object.assign(r.getCell(2), dataStyle("#,##0", BRAND_BLUE))
                r.getCell(3).value = d.revenue; Object.assign(r.getCell(3), dataStyle("₱#,##0.00", BRAND_TEAL))
                r.getCell(4).value = totalOrds > 0 ? d.orders / totalOrds : 0
                Object.assign(r.getCell(4), dataStyle("0.0%", BRAND_AMBER))
            })
            applyAltRows(ws, 5, 4 + peakHoursData.length, 4, [2])
        }

        // MARKETING ROI (branch_admin only)
        if (hasData(marketingData)) {
            const ws = wb.addWorksheet("Marketing ROI")
            ws.columns = [{ width: 18 }, { width: 18 }, { width: 18 }, { width: 14 }, { width: 18 }]
            addSheetTitle(ws, "Marketing ROI", "Channel spend, revenue attributed, and ROI", 5)

            const hr = ws.getRow(4)
            hr.values = ["Channel", "Spend (₱)", "Revenue Driven (₱)", "ROI %", "Net Profit (₱)"]
            hr.height = 22
            ;[1, 2, 3, 4, 5].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_PURPLE)))

            marketingData.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                const roi = d.spend > 0 ? (d.revenue - d.spend) / d.spend : 0
                r.getCell(1).value = d.channel; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = d.spend; Object.assign(r.getCell(2), dataStyle("₱#,##0.00", BRAND_CORAL))
                r.getCell(3).value = d.revenue; Object.assign(r.getCell(3), dataStyle("₱#,##0.00", BRAND_GREEN))
                r.getCell(4).value = roi; Object.assign(r.getCell(4), dataStyle("0.0%", roi >= 0 ? BRAND_GREEN : BRAND_CORAL, true))
                r.getCell(5).value = d.revenue - d.spend;  Object.assign(r.getCell(5), dataStyle("₱#,##0.00", roi >= 0 ? BRAND_TEAL : BRAND_CORAL))
            })

            const lastData = 4 + marketingData.length
            const totR = ws.getRow(lastData + 1)
            totR.height = 22
            totR.getCell(1).value = "TOTAL"; Object.assign(totR.getCell(1), { ...totalRowStyle(""), alignment: { horizontal: "left" } })
            totR.getCell(2).value = { formula: `=SUM(B5:B${lastData})` }; Object.assign(totR.getCell(2), totalRowStyle("₱#,##0.00"))
            totR.getCell(3).value = { formula: `=SUM(C5:C${lastData})` }; Object.assign(totR.getCell(3), totalRowStyle("₱#,##0.00"))
            totR.getCell(4).value = { formula: `=IFERROR((C${lastData+1}-B${lastData+1})/B${lastData+1},0)` }; Object.assign(totR.getCell(4), totalRowStyle("0.0%"))
            totR.getCell(5).value = { formula: `=C${lastData+1}-B${lastData+1}` }; Object.assign(totR.getCell(5), totalRowStyle("₱#,##0.00"))

            applyAltRows(ws, 5, lastData, 5)
        }

        if (hasData(menuItems)) {
            const ws = wb.addWorksheet("Menu Catalog")
            ws.columns = [{ width: 28 }, { width: 14 }, { width: 18 }, { width: 14 }]
            addSheetTitle(ws, "Menu Catalog", "All active menu items with pricing", 4)

            const hr = ws.getRow(4)
            hr.values = ["Item Name", "Category", "Classification", "Price (₱)"]
            hr.height = 22
            ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle("FF92400E")))

            menuItems.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                r.getCell(1).value = d.name; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = d.category; Object.assign(r.getCell(2), { ...labelStyle, font: { ...labelStyle.font, color: { argb: d.category === "Coffee" ? BRAND_AMBER : BRAND_TEAL }, bold: true } })
                r.getCell(3).value = d.classification; Object.assign(r.getCell(3), labelStyle)
                r.getCell(4).value = d.item_price; Object.assign(r.getCell(4), dataStyle("₱#,##0.00", BRAND_AMBER, true))
            })
            applyAltRows(ws, 5, 4 + menuItems.length, 4)
        }

        if (hasData(remakeData)) {
            const ws = wb.addWorksheet("Remake Requests")
            ws.columns = [{ width: 24 }, { width: 16 }, { width: 22 }]
            addSheetTitle(ws, "Remake Requests", "Quality issues flagged for remake", 3)
            const hr = ws.getRow(4)
            hr.values = ["Reason", "Status", "Created At"]
            hr.height = 22
            ;[1, 2, 3].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_CORAL)))
            remakeData.forEach((r, i) => {
                const row = ws.getRow(5 + i)
                row.height = 18
                row.getCell(1).value = r.reason || "Unspecified"; Object.assign(row.getCell(1), labelStyle)
                row.getCell(2).value = r.status || "—"; Object.assign(row.getCell(2), labelStyle)
                row.getCell(3).value = r.created_at ? new Date(r.created_at).toLocaleString("en-PH") : ""
                Object.assign(row.getCell(3), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
            })
            applyAltRows(ws, 5, 4 + remakeData.length, 3)
        }

        if (hasData(voidedData)) {
            const ws = wb.addWorksheet("Voided Items")
            ws.columns = [{ width: 22 }, { width: 12 }, { width: 20 }, { width: 24 }, { width: 24 }]
            addSheetTitle(ws, "Voided Items", `Line-item voids · Branch: ${shortBranch}`, 5)

            const hr = ws.getRow(4)
            hr.values = ["Void Reason", "Quantity", "Revenue Lost (₱)", "Void Requested At", "Void Decided At"]
            hr.height = 22
            ;[1, 2, 3, 4, 5].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_CORAL)))

            voidedData.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                const raw = (d.void_reason ?? "other").trim().toLowerCase()
                r.getCell(1).value = REASON_LABELS[raw] ?? d.void_reason ?? "Other"
                Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = Number(d.quantity)
                Object.assign(r.getCell(2), dataStyle("#,##0", BRAND_BLUE))
                r.getCell(3).value = Number(d.item_price) * Number(d.quantity)
                Object.assign(r.getCell(3), dataStyle("₱#,##0.00", BRAND_CORAL))
                r.getCell(4).value = d.void_requested_at
                    ? new Date(d.void_requested_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"
                Object.assign(r.getCell(4), { ...labelStyle, font: { ...labelStyle.font, color: { argb: "FF6B7280" }, size: 9 } })
                r.getCell(5).value = d.void_decided_at
                    ? new Date(d.void_decided_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"
                Object.assign(r.getCell(5), { ...labelStyle, font: { ...labelStyle.font, color: { argb: "FF6B7280" }, size: 9 } })
            })

            const lastData = 4 + voidedData.length
            const totR = ws.getRow(lastData + 1)
            totR.height = 22
            totR.getCell(1).value = "TOTAL"
            Object.assign(totR.getCell(1), { ...totalRowStyle(""), alignment: { horizontal: "left" } })
            totR.getCell(2).value = { formula: `=SUM(B5:B${lastData})` }
            Object.assign(totR.getCell(2), totalRowStyle("#,##0"))
            totR.getCell(3).value = { formula: `=SUM(C5:C${lastData})` }
            Object.assign(totR.getCell(3), totalRowStyle("₱#,##0.00"))

            const reasonMap = {}
            for (const d of voidedData) {
                const raw = (d.void_reason ?? "other").trim().toLowerCase()
                const label = REASON_LABELS[raw] ?? d.void_reason ?? "Other"
                if (!reasonMap[label]) reasonMap[label] = { count: 0, lost: 0 }
                reasonMap[label].count += 1
                reasonMap[label].lost  += Number(d.item_price) * Number(d.quantity)
            }
            const reasonRows = Object.entries(reasonMap).sort((a, b) => b[1].count - a[1].count)

            const summaryStart = lastData + 3
            ws.mergeCells(summaryStart, 1, summaryStart, 3)
            const miniTitle = ws.getCell(summaryStart, 1)
            miniTitle.value = "BREAKDOWN BY REASON"
            miniTitle.font  = { name: "Arial", bold: true, size: 9, color: { argb: WHITE } }
            miniTitle.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_CORAL } }
            miniTitle.alignment = { horizontal: "left", vertical: "middle", indent: 1 }
            ws.getRow(summaryStart).height = 18

            const miniHRow = ws.getRow(summaryStart + 1)
            miniHRow.height = 18
            ;["Reason", "Count", "Revenue Lost (₱)"].forEach((v, ci) => {
                miniHRow.getCell(ci + 1).value = v
                Object.assign(miniHRow.getCell(ci + 1), headerStyle("FF374151"))
            })
            reasonRows.forEach(([label, val], i) => {
                const r = ws.getRow(summaryStart + 2 + i)
                r.height = 17
                r.getCell(1).value = label; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = val.count; Object.assign(r.getCell(2), dataStyle("#,##0", BRAND_CORAL))
                r.getCell(3).value = val.lost;  Object.assign(r.getCell(3), dataStyle("₱#,##0.00", BRAND_CORAL))
            })
            applyAltRows(ws, summaryStart + 2, summaryStart + 1 + reasonRows.length, 3)
        }

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `CoffeeGusto_Report${exportLabel}_${exportDate}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <div className="p-6 font-sans" style={{ background: C.espresso }}>
            <Helmet><title>KaPerk</title></Helmet>
            <div className="flex flex-col gap-5" ref={dashboardRef}>
                <div className="flex flex-col gap-3 pb-4 border-b" style={{ borderColor: C.border }}>
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <RoleBadge role={role} branchName={branchName} />
                        <div className="flex flex-col gap-2 items-end min-w-0 w-full sm:w-auto">
                            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap min-w-0 w-full sm:w-auto justify-end">
                                <RangeSelector
                                    range={range} setRange={setRange}
                                    customStart={customStart} setCustomStart={setCustomStart}
                                    customEnd={customEnd}     setCustomEnd={setCustomEnd}
                                />
                                <div className="relative no-print flex-shrink-0" ref={menuRef}>
                                    <button
                                        onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                                        className="px-4 py-1.5 rounded-full text-[11px] border font-semibold flex items-center gap-1.5"
                                        style={{
                                            background: C.green,
                                            borderColor: C.green,
                                            color: "#fff",
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                            <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z"/>
                                        </svg>
                                        Download Report
                                    </button>
                                    {showDownloadMenu && (
                                        <div className="absolute right-0 mt-2 w-44 rounded-lg shadow-lg border z-50"
                                            style={{ background: C.card, borderColor: C.border }}>
                                            <button
                                                onClick={() => { handleDownloadPDF(); setShowDownloadMenu(false) }}
                                                disabled={pdfLoading}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg transition-colors"
                                                style={{ color: C.text, cursor: pdfLoading ? "not-allowed" : "pointer" }}
                                            >
                                                {pdfLoading ? (
                                                    <>
                                                        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                                            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4"/>
                                                            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z"/>
                                                        </svg>
                                                        Exporting…
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                            <path fill={C.text} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                            <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none"/>
                                                        </svg>
                                                        Download as PDF
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => { handleExport(); setShowDownloadMenu(false) }}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg transition-colors border-t"
                                                style={{ color: C.text, borderColor: C.border, cursor: "pointer" }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                    <path fill={C.text} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                    <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none"/>
                                                </svg>
                                                Download as Excel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {role === "staff" && (
                        <p className="m-0 text-[11px]" style={{ color: C.muted }}>
                            Showing operational data for your branch. Revenue and financial details are visible to branch admins only.
                        </p>
                    )}
                </div>

                {perms.showSalesRevenue && <SalesRevenue data={salesData ?? []} loading={salesLoading} />}
                {perms.showPaymentMethods && (
                    <PaymentMethodsBreakdown
                        methods={paymentStats.methods}
                        top={paymentStats.top}
                        totalAmount={paymentStats.totalAmount}
                        totalCount={paymentStats.totalCount}
                        refreshing={paymentMethodsLoading}
                    />
                )}
                {perms.showInventoryVariance && <InventoryLoss data={inventoryData ?? []} loading={inventoryLoading} />}
                {perms.showVoidedItems && <VoidedItems data={voidedData ?? []} loading={voidLoading} />}
                {perms.showRemakeRequests && <RemakeRequestsCard data={remakeData ?? []} loading={remakeLoading} />}
                {perms.showRewardsPullouts && (
                    <RewardsAndPullouts
                        redemptions={redemptionsData ?? []}
                        pullouts={pulloutsData ?? []}
                        loading={rewardsLoading}
                    />
                )}
                {perms.showPeakHours && <PeakHourAnalytics data={peakHoursData ?? []} loading={peakLoading} role={role} />}
                {perms.showMarketingROI && <MarketingROI data={marketingData ?? []} loading={mktLoading} />}
                {perms.showMenuCatalog && <MenuCatalog data={menuItems ?? []} loading={menuLoading} />}

            </div>

            {perms.showAIAnalyst && (
                <>
                    <AISidebar
                        open={aiSidebarOpen}
                        onClose={() => setAiSidebarOpen(false)}
                        summary={aiSummary}
                        recommendations={aiRecommendations}
                        loading={aiLoading}
                        error={aiError}
                        onGenerate={handleGenerateInsights}
                        hasData={hasData(salesData) || hasData(peakHoursData)}
                        history={history}
                        historyLoading={historyLoading}
                        onDeleteHistory={deleteAnalysis}
                        generatedAt={aiGeneratedAt}
                        generatedFor={aiGeneratedFor}
                    />
                    <AIFloatingButton
                        onClick={() => setAiSidebarOpen(true)}
                        hasNew={!!aiSummary}
                    />
                </>
            )}

            {pdfLoading && (
                <div style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.45)",
                    zIndex: 1100,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <div style={{
                        background: "#fff", borderRadius: 16,
                        padding: "28px 32px",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                        minWidth: 280, textAlign: "center",
                    }}>
                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 36, height: 36 }}>
                            <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="4" />
                            <path fill={pdfStatus === "generating_ai" ? AI : C.amber} d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <div>
                            <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: C.text }}>
                                {pdfStatus === "generating_ai" ? "Generating AI Analysis…" : "Building your PDF…"}
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                                {pdfStatus === "generating_ai"
                                    ? "Asking Gemini to interpret your data. This may take 10–20 seconds."
                                    : "Rendering charts and layout. Almost there…"}
                            </p>
                        </div>
                        {pdfStatus === "generating_ai" && (
                            <div style={{
                                padding: "8px 14px", borderRadius: 8,
                                background: AI + "10", border: `1px solid ${AI}22`,
                            }}>
                                <span style={{ fontSize: 11, color: AI }}>🤖 Gemini 2.5 Flash is reading your report</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}